import { scrollPageTo, scrollPageBy, scrollPageIntoView } from '../pageScroll';
import { beginCoachFlow, bindCoachFlowSubject, coachSequenceNow, failCoachFlow, markCoachFlow, noteCoachCanonicalRead, noteCoachContextMatch, noteCoachOutputCommitted, noteCoachWebSocketMessage } from '../../services/coachSequenceTiming';
import CoachMessageContent from './CoachMessageContent';
import { CoachActionCards } from '../CoachActionCard';
import React, { useState, useEffect, useCallback, useMemo, useRef, useLayoutEffect } from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';
import Activity from './Activity';
import NewActivityModal from '../NewActivityModal';
import RecommendationsModal from '../RecommendationsModal';
import FilterDropdown from './FilterDropdown';
import { filterActivitiesForFeed, isActivityTypeVisibleInFeed, parentTravelReplacementActivities } from './activityFeedFilters';
import { formatNutritionNumber } from './formatNutritionNumber';
import { getExceptionalBurnLabel } from './exceptionalBurnPresentation';
import { isDailyFeedRecommendation, isRecommendationForStatusContext } from './recommendationKind';
import StatusDropdown, { STATUS_OPTIONS } from './StatusDropdown';
import { pollForStatusInformedInsight } from './dailyInsightStatusPoll';
import { readAvailableDailyInsight } from './dailyInsightAvailability';
import { shouldAcceptStatusContext } from './statusContextOrdering';
import { shouldPollHostedDailyInsight } from './statusInsightRouting';
import { activityReviewPresenceFromTimelineMessage, canApplyCanonicalPresenceSnapshot, clearCoachReactionToast, coachAnswerFromRecommendation, coachReadyNotificationKey, coachReviewToastKey, deriveCoachThinking, findCoachAnswerInHome, findNewlyGeneratedCoachEvents, getCoachAskTypingSnapshot, getCoachHubOpenSnapshot, getCoachReactionToastSnapshot, getCoachReactionTypingEventIdsSnapshot, publishCoachReactionTyping, publishCoachThinking, reconcileActivityReviewPresence, reconcileCoachReactionTypingEventIds, shouldShowUnscopedActivityReviewToast, subscribeCoachAskTyping, subscribeCoachHubOpen, subscribeCoachReactionToast, subscribeCoachReactionTypingEvents } from './coachPresence';
import { coachReviewFailureFromTimelineMessage, completeCoachReviewToast, failCoachReviewToast, markCoachReviewToastExiting, removeCoachReviewToast, startCoachReviewToast } from './coachReviewToastLifecycle';
import OnboardingStory from '../OnboardingStory';
import PlanWizard from '../PlanWizard';
import { createPortal } from 'react-dom';
import TopBarActivityActions from './TopBarActivityActions';
import { useTodayAction } from './useTodayAction';
import { todayActivityId } from './todayNextAction';
import { isOngoingWorkoutActivity } from '../../utils/activityStatus';
import { ongoingShortcutWorkoutId } from './ongoingShortcutWorkout';
import { ActivityDetailModal } from './SocialActivityFeed';
import DojoHub from './DojoHub';
import { dojoCompletionCopy, dojoToastIdentityId } from './dojoCompletionToast';
import { useDojoToasts } from './useDojoToasts';
import './StandaloneDojoMedia.css';
import { projectDojoActivitiesByDate, getPersonalLateWorkoutPresentation } from './dojoActivityDayProjection';
import { mergeHydratedRosterComments, selectRosterCoachComments } from './dojoRosterComments';
import { addHistoricalDojoDayGroups } from './historicalDojoDayGroups';
import { coveredDojoDays, coveredDojoSourceActivities } from './coveredDojoDays';
import { compareActivitiesForDisplay, ACTIVITY_DISPLAY_TYPE_ORDER } from './activityDisplayOrder';
import { coachMessageFromInsight, findLatestCoachMessage } from './coachFeedMessage';
import { coachDayKey as getCoachDayKey } from './coachDayKey';
import useCoachHomePreview from './useCoachHomePreview';
import useDailyCoachTaskStatus from './useDailyCoachTaskStatus';
import DailyCoachFailureNotice from './DailyCoachFailureNotice';
import { notifyAppliedMediaRequirementUpdate } from '../../services/mediaRequirementPlanUpdates';
import DojoNudge from './DojoNudge';
import SocialPresenceRow from './SocialPresenceRow';
import SocialPresenceModal from './SocialPresenceModal';
import ActivitySocialBar from './ActivitySocialBar';
import CameraVideoMedia from './CameraVideoMedia';
import CameraMediaFeedStatus from './CameraMediaFeedStatus';
import CameraMediaCompletion from './CameraMediaCompletion';
import { useCameraMediaFeed } from './useCameraMediaFeed';
import { cameraCaptureDay, cameraPlacementDate, isCameraMediaActivity, isLinkedCameraParent, mergeCameraMedia } from '../../services/cameraMediaFeed';
import PenaltyCallout from './PenaltyCallout';
import DailyInsightProposal from './DailyInsightProposal';
import { canRegenerateDailyInsight } from './dailyInsightRegeneration';
import { CoachInlineTyping } from './coachInlineTyping';
import { getCoachImageVariantUrl } from '../../utils/coachImageVariants';
import DojoActivityToast from './DojoActivityToast';
import { CoachVisibilityProvider, CoachVisibleMessage, CoachToastGate, DailyInsightCue, DailyInsightToastGate } from './coachVisibleMessage';
import SocialProfileModal from '../SocialProfileModal';
import CoachHubModal from '../CoachHubModal';
import { coachReadyToastTargetId, coachToastFocusOptions } from '../coachHubScroll';
import DojoHomeModal from '../DojoHomeModal';
import DojoNutritionIndicators from './DojoNutritionIndicators';
import { buildDojoNutritionMembers } from './dojoNutritionModel';
import { buildDojoActivityTypeMembers, resolveDojoActivityTypeMember } from './dojoActivityTypeModel';
import { buildDojoActivityTypeGroups } from './dojoActivityTypeGroups';
import DojoActivityTypeSection from './DojoActivityTypeSection';
import { getDojoActivityStatus } from './dojoActivityStatus';
import { statusCheckInErrorMessage } from '../statusCheckInError';
import { ActivityHeaderSummary, ActivityStatusBullet, isDefaultExpanded } from './ActivityHeaderSummary';
import ActivityPositionPin from './ActivityPositionPin';
import PeerActivityDisclosure, { peerActivityDisclosureTargetId } from './PeerActivityDisclosure';
import { createWebSocketAuthLifecycle } from './websocketAuthLifecycle';
import { calculateComplianceScore } from '../../utils/Nutrition';
import { getNutritionScorePresentation } from './nutritionScorePresentation';
import { useFeature } from '../../contexts/SubscriptionContext';
import { shouldShowDojoNudge } from './dojoMembershipState';
import { refreshApprovedWorkoutActivities } from './workoutProposalReconciliation';
import { mealMoveModeVisibility } from './mealMoveModeVisibility';
import { isActivityInteractionLocked } from './activityInteractionLock';
import { isActivityNameOverflow } from './activityNameMarquee';
import {
  canStartInitialActivityRetry,
  getActivityStartupPresentation,
  getActivityStartupRequestPlan,
  getCachedActivityProfile,
  isLatestStartupRequest,
  shouldShareStartupProfileRequest,
} from './activityStartupPresentation';
import CoachAvatar from '../CoachAvatar';
import CoachFeedLoadingSlot from './CoachFeedLoadingSlot';
import {
  loadDojoFeedProgressively,
  mergeDojoFeedHydration,
  mergeDojoMembershipContext,
} from './dojoFeedStartup';
import ActivityPaginationLoading from './ActivityPaginationLoading';
import "./Activities.css";
import { API_BASE_URL } from '../../config';
import { MoveFoodsProvider, useMoveFoodsOptional } from './MoveFoodsContext';
import { isForeground, subscribeForeground, useForegroundInterval } from '../../services/AppStateService';
import { mergeSocialPresenceByDate, shouldApplyActivityRevision } from '../socialPresencePresentation';
import { isTravelReplacementAvailable } from './travelReplacementAvailability';
import { isTravelReplacementEnabledForService } from './travelReplacementAvailability';
import { findFocusedActivityElement } from './activityFocusTarget';
import { reconcileActivitySnapshot, reconcileDayStatusSnapshot } from './activityFeedReconciliation';
import { createWorkoutStartReconciliation } from './workoutStartReconciliation';
import { WorkoutStartContext } from './WorkoutStartContext';
import { shouldContinueActivityFetch } from './activityFeedPagination';
import {
  COMPLETION_SHIMMER_DURATION_MS,
  getCenteredActivityScrollTop,
  getCompletionScrollBehavior,
  scheduleCompletionCelebration,
} from './completionCelebration';
import {
  clearActivityScrollAnchor,
  findActivityScrollElement,
  getActivityScrollId,
  readActivityScrollAnchor,
  restoreActivityScrollAnchor,
  waitForActivityLoadIdle,
} from '../../services/activityScrollRestoration';

const ORDERED_TYPES = ACTIVITY_DISPLAY_TYPE_ORDER;

// Where a jumped-to row lands below the viewport top, and how long the
// smooth scroll gets to settle before visibility takes over as the anchor.
const JUMP_OFFSET = 140;
const ENABLE_ACTIVITY_JUMP_BUTTONS = false;

const ONGOING_STATUSES = ['ongoing', 'in_progress', 'started'];
const INITIAL_ACTIVITY_REQUEST_TIMEOUT_MS = 15000;

// Still actionable: not done, not written off, and not past its due date
// (expired activities render as failed and can no longer be completed).
const isPendingActivity = (activity) => {
  if (!activity) return false;
  if (['complete', 'rest', 'failed', 'skipped'].includes(activity.status)) return false;
  const dueAt = activity.due_at ? new Date(activity.due_at).getTime() : null;
  return !(dueAt && dueAt < Date.now());
};

// Type icons for visual identification
const TYPE_CONFIG = {
  workout: { icon: '💪', label: 'Workout', color: '#ef4444' },
  meal: { icon: '🍽️', label: 'Meal', color: '#22c55e' },
  media: { icon: '📸', label: 'Media', color: '#8b5cf6' },
  metric: { icon: '📊', label: 'Metric', color: '#3b82f6' },
  survey: { icon: '📝', label: 'Survey', color: '#f59e0b' },
  other: { icon: '📌', label: 'Activity', color: '#6b7280' }
};

function ActivityNameLabel({ name, partLabel = '' }) {
  const shellRef = useRef(null);
  const contentRef = useRef(null);
  const [isMarquee, setIsMarquee] = useState(false);
  const fullName = `${name}${partLabel}`;

  const measure = useCallback(() => {
    const shell = shellRef.current;
    const content = contentRef.current;
    if (!shell || !content) return;
    setIsMarquee(isActivityNameOverflow({
      contentWidth: content.getBoundingClientRect().width,
      availableWidth: shell.clientWidth,
    }));
  }, []);

  useLayoutEffect(() => {
    measure();
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(measure);
    if (observer && shellRef.current) observer.observe(shellRef.current);
    window.addEventListener('resize', measure);
    return () => {
      observer?.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, [fullName, measure]);

  return (
    <span ref={shellRef} className={`todo-name-text ${isMarquee ? 'is-marquee' : ''}`}>
      <span className="todo-name-track">
        <span ref={contentRef}>{name}{partLabel && <span className="todo-part-label">{partLabel}</span>}</span>
        {isMarquee && <span aria-hidden="true">{fullName}</span>}
      </span>
    </span>
  );
}

const getDateKeyInTimezone = (date, timezone = 'UTC') => {
  try {
    return new Date(date).toLocaleDateString('en-CA', { timeZone: timezone });
  } catch (e) {
    return new Date(date).toISOString().split('T')[0];
  }
};

const formatDateInTimezone = (date, timezone = 'UTC', locale = undefined, options = {}) => {
  try {
    return new Date(date).toLocaleDateString(locale, { ...options, timeZone: timezone });
  } catch (e) {
    return new Date(date).toLocaleDateString(locale, options);
  }
};

// Collapsible Todo List - grouped by activity type
export const TodoList = ({
  dayGroup, 
  exercises, 
  modalService, 
  backendService, 
  onOpenPlanEditor, 
  onActivityComplete, 
  onActivityCancel, 
  onActivityRefresh,
  nutritionTotals,
  nutritionTargets,
  complianceScore,
  isRecentDay,
  getDateKeyForUser,
  timezone,
  recommendationVersion,
  coachImageUrl = null,
  coachEmoji = '🥋',
  coachIdentitySettled = false,
  travelReplacementEnabled = false,
  activityInteractionLock = null,
  onWorkoutReorderModeChange,
  dojoActivities = [],
  dojoNutritionActivities = [],
  dojoUsers = [],
  currentUserId = null,
  filters = {},
  revealRequest = null,
  onCameraMediaStatus = null,
  cameraMediaMutation = null,
}) => {
  // One selection owns both the nutrition summary and Meals rows. Scope it to
  // viewer/day/roster so an old peer choice cannot survive a context change.
  const selectionScope = JSON.stringify([currentUserId, dayGroup.dateKey, dojoUsers.filter(Boolean).map(u => String(u._id || u.user_id || u.id)).sort()]);
  const [mealSelection, setMealSelection] = useState(null);
  const [activitySelections, setActivitySelections] = useState({});
  useEffect(() => {
    setMealSelection(null);
    setActivitySelections({});
  }, [selectionScope]);
  const selfId = String(currentUserId || '__personal__');
  const roster = dojoUsers.some(u => u && String(u._id || u.user_id || u.id) === selfId) ? dojoUsers : [{ _id: selfId, display_name: 'You' }, ...dojoUsers];
  const nutritionMembers = buildDojoNutritionMembers({ users: roster, activities: dojoNutritionActivities, currentUserId: selfId, dateKey: dayGroup.dateKey, todayKey: getDateKeyForUser(new Date()), personal: { totals: nutritionTotals, targets: nutritionTargets, score: complianceScore } });
  const selectedMember = nutritionMembers.find(m => mealSelection?.scope === selectionScope && m.id === mealSelection.id) || nutritionMembers.find(m => m.isSelf);
  const peerMeals = selectedMember.isSelf ? [] : [...new Map(dojoNutritionActivities.filter(a =>
    a._id && a.type === 'meal' && !a.is_template && String(a.user_id) === selectedMember.id
    // Prefer owner-local nutrition context. Other authorized rows retain the
    // existing feed's day projection; do not invent dates or fetch private rows.
    && (a.dojo_nutrition?.day_key ? a.dojo_nutrition.day_key === dayGroup.dateKey : dojoActivities.some(dayActivity => String(dayActivity._id) === String(a._id)))
  ).map(a => [String(a._id), a])).values()].sort((a, b) => compareActivitiesForDisplay('meal', a, b));

  // Expansion model (simplified May 2026):
  //   - Pending activities are expanded by default.
  //   - Completed / rest activities are collapsed by default.
  //   - User clicks set a per-id override that flips the default.
  // The previous "one-open-per-type accordion" was abandoned because
  // the choreography (auto-collapse, auto-expand-next, animated
  // hand-off) was too fragile and confused users when their action
  // moved a card they were actively editing.
  // Map<activityId, boolean> — boolean is the explicit user override.
  const [expandOverrides, setExpandOverrides] = useState(() => new Map());
  const appliedRevealRef = useRef(null);
  useEffect(() => {
    if (!revealRequest?.id || activityInteractionLock || appliedRevealRef.current === revealRequest) return;
    const exists = Object.values(dayGroup.types).flat().some(a => todayActivityId(a) === revealRequest.id);
    if (exists) {
      appliedRevealRef.current = revealRequest;
      setExpandOverrides(previous => new Map(previous).set(revealRequest.id, true));
    }
  }, [revealRequest, activityInteractionLock, dayGroup.types]);

  // While the per-day Move Foods mode is active, all meal activities
  // must stay expanded so foods are visible/draggable across siblings.
  const moveFoods = useMoveFoodsOptional();

  // One-shot shimmer for activities that just transitioned to complete.
  // We add the id to this set, then auto-clear it ~1.2s later so the
  // shimmer animation only plays once. Persisted across re-renders so
  // the transition can complete even though the activity row's status
  // changes synchronously.
  const [shimmerIds, setShimmerIds] = useState(() => new Set());
  const completionTimersRef = useRef(new Set());
  const completionAnimationFramesRef = useRef(new Set());
  const completionSettleCleanupsRef = useRef(new Set());

  useEffect(() => () => {
    completionAnimationFramesRef.current.forEach(frame => cancelAnimationFrame(frame));
    completionAnimationFramesRef.current.clear();
    completionSettleCleanupsRef.current.forEach(cleanup => cleanup());
    completionSettleCleanupsRef.current.clear();
    completionTimersRef.current.forEach(timer => clearTimeout(timer));
    completionTimersRef.current.clear();
  }, []);

  // Track each activity's previous status so we can detect the moment
  // a pending activity flips to complete. When that happens we play a
  // one-shot shimmer, clear any "expanded" override (so the default-
  // collapsed rule for completed activities takes effect), and bring
  // the row back into view so the user sees the celebration. The next
  // pending activity is already expanded by default, so no manual
  // hand-off is needed.
  const prevStatusRef = useRef({});
  useEffect(() => {
    const prev = prevStatusRef.current;
    const next = {};
    const justCompleted = []; // [{ type, id }]

    ORDERED_TYPES.forEach(type => {
      const items = dayGroup.types[type] || [];
      items.forEach(activity => {
        const id = activity._virtualId || activity._id;
        const key = `${type}:${id}`;
        next[key] = activity.status;
        if (prev[key] && prev[key] !== 'complete' && activity.status === 'complete') {
          justCompleted.push({ type, id });
        }
      });
    });
    prevStatusRef.current = next;

    if (justCompleted.length === 0) return;

    // ----- Step 1 (immediate): clear any "expanded" override on
    // just-completed rows so the default-collapsed rule takes over.
    // Center the settled row first; the shimmer starts only after the
    // smooth scroll has had time to settle the user's attention.

    // Capture the just-completed row's pre-collapse viewport top so
    // we can anchor it through the layout change (its expanded card
    // disappearing pulls everything below upward — without
    // compensation the row visibly jumps before the smooth scroll
    // even starts).
    const first = justCompleted[0];
    const anchorEl = document.getElementById(`activity-${first.id}`);
    const beforeTop = anchorEl ? anchorEl.getBoundingClientRect().top : null;

    setExpandOverrides(prev => {
      let changed = false;
      const next = new Map(prev);
      justCompleted.forEach(({ id }) => {
        if (next.has(id)) {
          next.delete(id);
          changed = true;
        }
      });
      return changed ? next : prev;
    });

    // Center the just-completed row. When the user
    // completes a meal/workout from the bottom of an expanded card,
    // collapsing leaves the page scrolled well past the row — they'd
    // miss the shimmer entirely. Centering creates clear separation from
    // the top toast stack and gives the completion moment visual priority.
    // Two RAFs let the collapse layout settle first;
    // otherwise we'd scroll relative to the pre-collapse position.
    const firstFrame = requestAnimationFrame(() => {
      completionAnimationFramesRef.current.delete(firstFrame);
      const secondFrame = requestAnimationFrame(() => {
        completionAnimationFramesRef.current.delete(secondFrame);
        const el = document.getElementById(`activity-${first.id}`);
        if (!el) return;

        // Anchor: instant compensation so the row appears not to
        // have moved through the collapse, then smooth-scroll to the
        // comfort offset. Avoids the perceived double-jump.
        if (beforeTop !== null) {
          const afterTop = el.getBoundingClientRect().top;
          const delta = afterTop - beforeTop;
          if (Math.abs(delta) > 1) {
            scrollPageBy({ top: delta, left: 0, behavior: 'instant' });
          }
        }

        const rect = el.getBoundingClientRect();
        const centeredTop = getCenteredActivityScrollTop({
          rect,
          pageYOffset: window.pageYOffset,
          viewportHeight: window.innerHeight,
        });
        const prefersReducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches === true;
        scrollPageTo({
          top: centeredTop,
          behavior: getCompletionScrollBehavior(prefersReducedMotion),
        });

        let cancelSettle = null;
        cancelSettle = scheduleCompletionCelebration({
          target: window,
          onReady: () => {
            completionSettleCleanupsRef.current.delete(cancelSettle);
            setShimmerIds(s => {
              const out = new Set(s);
              justCompleted.forEach(({ id }) => out.add(id));
              return out;
            });

            const clearShimmerTimer = setTimeout(() => {
              completionTimersRef.current.delete(clearShimmerTimer);
              setShimmerIds(s => {
                const out = new Set(s);
                justCompleted.forEach(({ id }) => out.delete(id));
                return out;
              });
            }, COMPLETION_SHIMMER_DURATION_MS);
            completionTimersRef.current.add(clearShimmerTimer);
          },
        });
        completionSettleCleanupsRef.current.add(cancelSettle);
      });
      completionAnimationFramesRef.current.add(secondFrame);
    });
    completionAnimationFramesRef.current.add(firstFrame);
  }, [dayGroup]);
  
  // Historical shared media is an authorized peer source, never personal data.
  const sharedMedia = (dayGroup.sharedMedia || []).filter(activity =>
    activity?.type === 'media' && activity._id && activity.user_id
    && currentUserId && String(activity.user_id) !== String(currentUserId)
    && dojoUsers.some(user => user && String(user._id || user.user_id || user.id) === String(activity.user_id))
    && !(dayGroup.types.media || []).some(own => String(own._id) === String(activity._id))
  );
  // Group activities by type. Dojo rows are already privacy-filtered by the
  // backend; apply the viewer's current feed filters before using them to expose
  // a peer-only section or member status.
  // Prefer the current day feed on overlap. Member construction deduplicates
  // by owner/type/id; both sources must pass the same active feed filters.
  const peerVideos = (dayGroup.types.media || []).filter(activity => isCameraMediaActivity(activity)
    && String(activity.user_id) !== selfId);
  const personalDayTypes = { ...dayGroup.types, media: (dayGroup.types.media || []).filter(activity => !peerVideos.includes(activity)) };
  const rosterIds = new Set(roster.filter(Boolean).map(user => String(user._id || user.user_id || user.id)));
  const otherSharedVideos = peerVideos.filter(activity => !rosterIds.has(String(activity.user_id)));
  const visibleDojoActivities = filterActivitiesForFeed([...sharedMedia, ...peerVideos.filter(activity => rosterIds.has(String(activity.user_id))), ...dojoActivities], filters);
  const groupedByType = buildDojoActivityTypeGroups({
    dayTypes: personalDayTypes,
    visibleDojoActivities,
  });
  if (otherSharedVideos.length && !groupedByType.media) groupedByType.media = { items: [], total: 0 };
  Object.entries(groupedByType).forEach(([type, group]) => {
    group.config = TYPE_CONFIG[type] || TYPE_CONFIG.other;
  });
  
  const toggleActivity = (activity) => {
    const id = activity._virtualId || activity._id;
    // Anchor the clicked row's viewport position across the layout
    // change so the row visually stays put through the expand/collapse.
    const clickedEl = document.getElementById(`activity-${id}`);
    const beforeTop = clickedEl ? clickedEl.getBoundingClientRect().top : null;

    const currentlyExpanded = expandOverrides.has(id)
      ? expandOverrides.get(id)
      : isDefaultExpanded(activity);

    setExpandOverrides(prev => {
      const next = new Map(prev);
      next.set(id, !currentlyExpanded);
      return next;
    });

    const HEADER_OFFSET = 160;
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const el = document.getElementById(`activity-${id}`);
        if (!el) return;

        // Step 1: instant compensation so the row visually stays put
        // through the expand/collapse layout shift.
        if (beforeTop !== null) {
          const afterTop = el.getBoundingClientRect().top;
          const delta = afterTop - beforeTop;
          if (Math.abs(delta) > 1) {
            scrollPageBy({ top: delta, left: 0, behavior: 'instant' });
          }
        }

        // Step 2: smooth-scroll to the read-friendly offset if the
        // anchored position isn't already in the comfort zone.
        const rect = el.getBoundingClientRect();
        const viewportH = window.innerHeight;
        if (rect.top < HEADER_OFFSET || rect.top > viewportH * 0.5) {
          const y = rect.top + window.pageYOffset - HEADER_OFFSET;
          scrollPageTo({ top: y, behavior: 'smooth' });
        }
      });
    });
  };
  
  // Broadcast videos are typed attachments of their Media parent: the camera
  // card owns playback, audience, typed deletion and its own nonowner-aware social bar.
  const renderCameraContent = (activity, embedded) => {
    const isOwner = String(activity.user_id) === String(currentUserId);
    const author = dojoUsers.find(user => user && String(user._id || user.user_id || user.id) === String(activity.user_id));
    return <CameraVideoMedia activity={activity} authorName={isOwner ? 'You' : author?.display_name || 'Member'} backendService={backendService} isOwner={isOwner} timeZone={timezone} embedded={embedded} />;
  };
  // The ordinary activity shell (status, name, expand/collapse) around a camera
  // parent. Only its owner gets the persisted complete/reopen control, and only
  // for a linked parent; legacy unlinked videos and peers stay read-only.
  const renderCameraActivity = activity => {
    const id = activity._id;
    const isExpanded = expandOverrides.has(id) ? expandOverrides.get(id) : isDefaultExpanded(activity);
    const isDone = getDojoActivityStatus(activity).status === 'done';
    const videos = Array.isArray(activity.data.videos) && activity.data.videos.length ? activity.data.videos : [activity.data.video];
    const lifecycle = onCameraMediaStatus && isLinkedCameraParent(activity) && String(activity.user_id) === String(currentUserId);
    return (
      <div key={id} id={`activity-${id}`} tabIndex={-1} className={`todo-activity camera-media-activity ${isDone ? 'done' : ''} ${isExpanded ? 'expanded' : ''}`}>
        <div className="todo-activity-row" onClick={() => toggleActivity(activity)}>
          <ActivityStatusBullet activity={activity} />
          <span className="todo-name with-late-note"><ActivityNameLabel name={activity.name || 'Video'} /></span>
          {!isExpanded && videos.some(video => video.state === 'live') && <span className="media-video-live-badge">Live</span>}
          <span className={`todo-chevron ${isExpanded ? 'open' : ''}`}>›</span>
        </div>
        {isExpanded && (
          <div className="todo-content">
            {renderCameraContent(activity, true)}
            {lifecycle && <CameraMediaCompletion activity={activity} mutation={cameraMediaMutation?.(id) || null} onStatus={onCameraMediaStatus} />}
          </div>
        )}
      </div>
    );
  };

  const renderActivity = (activity, type) => {
    if (isCameraMediaActivity(activity)) return renderCameraActivity(activity);
    const activityKey = activity._virtualId || activity._id;
    const forceExpandForMove = moveFoods.isActive && type === 'meal';
    const moveVisibility = mealMoveModeVisibility({ type, moveActive: moveFoods.isActive });
    const isExpanded = forceExpandForMove
      ? true
      : (expandOverrides.has(activityKey)
          ? expandOverrides.get(activityKey)
          : isDefaultExpanded(activity));
    const presentation = getDojoActivityStatus(activity);
    const isDone = presentation.status === 'done';
    const isRest = presentation.status === 'rest';
    const isOngoingWorkout = presentation.status === 'live';
    const isExpired = presentation.status === 'missed';
    const config = TYPE_CONFIG[type] || TYPE_CONFIG.other;
    const activityName = activity.name || config.label;
    const activityInteractionDisabled = isActivityInteractionLocked({
      lock: activityInteractionLock,
      activityId: activityKey,
      type,
      dayKey: dayGroup.dateKey,
    });
    const hasPlan = activity.source?.activity_plan_id;
    const isMultipart = activity._totalParts > 1;
    const lateWorkout = getPersonalLateWorkoutPresentation(
      activity,
      dayGroup.dateKey,
      getDateKeyForUser,
      new Date()
    );
    
    const handlePlanClick = (e) => {
      e.stopPropagation();
      if (onOpenPlanEditor && hasPlan) {
        onOpenPlanEditor({ 
          planId: activity.source.activity_plan_id, 
          entryId: activity.source.activity_plan_entry_id 
        });
      }
    };
    
    return (
      <div
        key={activityKey}
        id={`activity-${activityKey}`}
        tabIndex={-1}
        data-scroll-activity-id={getActivityScrollId(activity) || undefined}
        className={`todo-activity ${activityInteractionDisabled ? 'activity-interaction-locked' : ''} ${isDone ? 'done' : ''} ${lateWorkout ? lateWorkout.className : ''} ${isRest ? 'rest' : ''} ${isExpired ? 'failed' : ''} ${isOngoingWorkout ? 'ongoing-workout' : ''} ${isExpanded ? 'expanded' : ''} ${shimmerIds.has(activityKey) ? 'just-completed' : ''}`}
        aria-disabled={activityInteractionDisabled || undefined}
        inert={activityInteractionDisabled ? '' : undefined}
      >
        <div className="todo-activity-row" onClick={() => !activityInteractionDisabled && toggleActivity(activity)}>
          <ActivityStatusBullet activity={activity} />
          <span className="todo-name with-late-note">
            <ActivityNameLabel
              name={activityName}
              partLabel={isMultipart ? ` (Part ${activity._partNumber} of ${activity._totalParts})` : ''}
            />
            {lateWorkout && <span className="todo-workout-subtitle">
              <span className="todo-late-note">{lateWorkout.detail}</span>
            </span>}
          </span>
          <ActivityHeaderSummary activity={activity} hideNutrition={moveVisibility.hideHeaderNutrition} />
          <ActivityPositionPin activity={activity} />
          {activity.social && !activityInteractionLock && !moveVisibility.hidePlanAndSocial && <ActivitySocialBar activityId={activity._id} social={activity.social} backendService={backendService} compact={true} isOwner={true} activityStatus={activity.status} />}
          <span className={`todo-chevron ${isExpanded ? 'open' : ''}`}>›</span>
        </div>
        {isExpanded && (
          <div className="todo-content">
            <Activity
              activity={activity}
              navigationRequest={!activityInteractionLock && revealRequest?.id === todayActivityId(activity) ? revealRequest : null}
              exercises={exercises}
              modalService={modalService}
              backendService={backendService}
              onOpenPlanEditor={onOpenPlanEditor}
              onActivityComplete={onActivityComplete}
              onActivityCancel={onActivityCancel}
              onActivityRefresh={onActivityRefresh}
              onReorderModeChange={active => onWorkoutReorderModeChange?.(activityKey, active)}
              suppressActions={activityInteractionLock?.kind === 'workout-reorder' && String(activityInteractionLock.activityId) === String(activityKey)}
              recommendationVersion={recommendationVersion}
              coachImageUrl={coachImageUrl}
              coachEmoji={coachEmoji}
              coachIdentitySettled={coachIdentitySettled}
              travelReplacementEnabled={travelReplacementEnabled}
              timezone={timezone}
              compact={true}
              embedded={true}
            />
            {hasPlan && !activityInteractionLock && !moveVisibility.hidePlanAndSocial && (
              <div className="center-button pad-button-up-small">
                <button 
                  type="button" 
                  onClick={handlePlanClick}
                  style={{ 
                    background: 'transparent', 
                    border: 'none', 
                    color: '#6b7280', 
                    fontSize: '0.8rem', 
                    cursor: 'pointer', 
                    textDecoration: 'none',
                    opacity: 0.7,
                    padding: '4px 8px'
                  }}
                  onMouseOver={(e) => { e.target.style.opacity = '1'; e.target.style.textDecoration = 'underline'; }}
                  onMouseOut={(e) => { e.target.style.opacity = '0.7'; e.target.style.textDecoration = 'none'; }}
                >
                  Edit in Plan
                </button>
              </div>
            )}
            {activity.social && !activityInteractionLock && !moveVisibility.hidePlanAndSocial && (
              <ActivitySocialBar activityId={activity._id} social={activity.social} backendService={backendService} isOwner={true} activityStatus={activity.status} />
            )}
          </div>
        )}
      </div>
    );
  };

  const renderPeerActivityDetail = activity => isCameraMediaActivity(activity) ? renderCameraContent(activity, false) : (
    <Activity
      activity={activity}
      exercises={exercises}
      modalService={modalService}
      backendService={backendService}
      timezone={timezone}
      readOnly
      compact
      embedded
    />
  );
  
  const mealTypeVisible = isActivityTypeVisibleInFeed(filters, 'meal');
  const hasNutritionSummary = mealTypeVisible && Boolean((nutritionTargets && nutritionTotals) || dojoUsers.length > 0);
  // Keep nutrition inside Meals when Meals are in the active feed scope, even
  // when no meal rows exist for the day. An explicit non-meal type filter must
  // not leak the summary-only Meals section into that filtered feed.
  if (hasNutritionSummary && !groupedByType.meal) {
    groupedByType.meal = { config: TYPE_CONFIG.meal, items: [], total: 0 };
  }
  const typeOrder = ORDERED_TYPES.filter(type => groupedByType[type]);
  if (typeOrder.length === 0 && !hasNutritionSummary) return null;
  
  // Helper to determine group status class
  const getGroupStatusClass = (type, items) => {
    const states = items.map(activity => getDojoActivityStatus(activity));
    const countable = states.filter(state => state.countable);
    if (!countable.length) return '';
    if (countable.every(state => state.completedForDay)) return 'status-complete';
    if (countable.some(state => state.status === 'missed')) return 'status-expired';
    return 'status-pending';
  };
  
  return (
    <div className="todo-list">
      {typeOrder.map(type => {
        const group = groupedByType[type];
        const personalTypeItems = personalDayTypes[type] || [];
        const activityMembers = type === 'meal' ? nutritionMembers : buildDojoActivityTypeMembers({
          users: roster,
          activities: visibleDojoActivities,
          personalActivities: personalTypeItems,
          type,
          currentUserId: selfId,
        });
        const selectedActivityMember = type === 'meal'
          ? selectedMember
          : resolveDojoActivityTypeMember(activityMembers, activitySelections[type], selectionScope);

        if (type !== 'meal') {
          return <React.Fragment key={type}><DojoActivityTypeSection
            type={type}
            config={group.config}
            members={activityMembers}
            selectedMember={selectedActivityMember}
            onSelect={id => setActivitySelections(previous => ({
              ...previous,
              [type]: { scope: selectionScope, id },
            }))}
            backendService={backendService}
            personalItems={group.items}
            renderPersonalActivity={renderActivity}
            renderPeerActivityDetail={renderPeerActivityDetail}
            renderActivityName={name => <ActivityNameLabel name={name} />}
            selectionScope={selectionScope}
            showMembers={dojoUsers.length > 0}
            selfStatusClass={group.items.length ? getGroupStatusClass(type, group.items) : ''}
          />{type === 'media' && otherSharedVideos.length > 0 && <div className="todo-type-items" aria-label="Shared videos">
            {otherSharedVideos.map(activity => renderActivity(activity, type))}
          </div>}</React.Fragment>;
        }

        const selectedPeerMeals = !selectedMember.isSelf;
        const visibleItems = group.items;
        const statusClass = selectedPeerMeals || visibleItems.length === 0 ? '' : getGroupStatusClass(type, visibleItems);

        return (
          <div key={type} className={`todo-type-group open ${statusClass}`}>
            <div className="todo-type-header">
              <span className="todo-type-icon">{group.config.icon}</span>
              <span className="todo-type-label">Meals</span>
            </div>
            <div className="todo-type-items">
              {hasNutritionSummary && (
                <div className="todo-nutrition-summary">
                  <DailyNutritionSummary key={`${dayGroup.dateKey}:${currentUserId}`} meals={dayGroup.types.meal || []} isRecentDay={isRecentDay}
                    members={nutritionMembers} selected={selectedMember} showMembers={dojoUsers.length > 0} onSelect={id => setMealSelection({ scope: selectionScope, id })} />
                </div>
              )}
              {selectedPeerMeals ? (peerMeals.length ? peerMeals.map(activity => <PeerActivityDisclosure
                key={`${selectionScope}:${selectedMember.id}:${activity._id}:${peerActivityDisclosureTargetId(activity)}`}
                activity={activity}
                selectedMemberContext
                owner={selectedMember.user}
                backendService={backendService}
                renderActivityDetail={renderPeerActivityDetail}
                renderActivityName={name => <ActivityNameLabel name={name} />}
              />) : <p className="nutrition-unknown">No meals available for this day.</p>)
                : visibleItems.map(activity => renderActivity(activity, type))}
              {selectedPeerMeals && (!selectedMember.totals || peerMeals.some(meal => meal.nutrition_summary_unavailable || (['complete', 'completed'].includes(meal.status) && !meal.dojo_nutrition?.targets))) && <p className="nutrition-unknown nutrition-coverage-note">{selectedMember.totals ? 'Nutrition is unavailable for some displayed entries.' : 'Nutrition unavailable'}</p>}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export const DailyNutritionSummary = ({ meals = [], isRecentDay = true, members, selected, showMembers, onSelect }) => {
  const peer = !selected.isSelf;
  return <div className="daily-nutrition-summary">
    <div className="nutrition-summary-heading"><strong>Daily Nutrition</strong>
    </div>
    {showMembers && <DojoNutritionIndicators members={members} selectedId={selected.id} onSelect={onSelect} />}
    <div className="nutrition-selected-summary" role="region" aria-label={`${peer ? selected.user.display_name || 'Member' : 'You'} nutrition`}>
      {selected.totals && selected.targets
        ? <NutritionBars totals={selected.totals} targets={peer ? { ...selected.targets, calories_min: selected.calMin, calories_max: selected.calMax } : selected.targets} score={selected.score} status={selected.status} meals={peer ? [] : meals} isRecentDay={peer || isRecentDay} peer={peer} />
        : peer ? <div className="nutrition-bars-unavailable" aria-label="Nutrition unavailable" /> : <p className="nutrition-unknown">Nutrition unavailable</p>}
    </div>
  </div>;
};

const NutritionBars = ({ totals, targets, score, status, meals, isRecentDay, peer }) => {

  const exceptionalBurnBonus = Number(targets?.details?.exceptional_burn_bonus) || 0;
  const exceptionalBurnLabel = getExceptionalBurnLabel(targets);

  const calMin = targets.calories_min ?? targets.calories;
  const calMax = targets.calories_max ?? targets.calories;
  // Protein is minimum-only (no max)
  const proMin = targets.protein;
  
  // Visual Scale: 0 to MaxLimit (where MaxLimit is slightly above TargetMax to accommodate overflow)
  // Default scale: TargetMax is at ~85% of the bar width.
  const visualMax = Math.max(calMax / 0.85, totals.calories * 1.1);
  const getPct = (val) => Math.min(100, Math.max(0, (val / visualMax) * 100));
  
  // For protein, show the minimum at 75% of bar so there's room to show excess
  const proVisualMax = Math.max(proMin / 0.75, totals.protein * 1.1);
  const getProPct = (val) => Math.min(100, Math.max(0, (val / proVisualMax) * 100));

  const minPct = getPct(calMin);
  const maxPct = getPct(calMax);
  const currentPct = getPct(totals.calories);
  
  const proMinPct = getProPct(proMin);
  const proCurrentPct = getProPct(totals.protein);
  
  // Check if nutrition goals are met
  const caloriesInRange = totals.calories >= calMin && totals.calories <= calMax;
  const proteinSatisfied = totals.protein >= proMin;
  
  // Colors - green when on target
  let barColor = '#3b82f6'; // Blue (Under)
  if (caloriesInRange) barColor = '#10b981'; // Green when in range
  if (totals.calories > calMax) barColor = '#ef4444'; // Red (Over)
  
  // Protein: green if met, purple if under
  let proBarColor = proteinSatisfied ? '#10b981' : '#8b5cf6';

  // Check if there are any incomplete meals
  const hasIncompleteMeals = meals.some(m => m.status !== 'complete' && m.status !== 'completed');

  const { color: badgeColor, background: badgeBg } = getNutritionScorePresentation({
    score,
    status,
    totals,
    targets: { ...targets, calories_max: calMax },
  });

  return (
    <div className="nutrition-bars">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
        <span />
        <span 
          className={`nutrition-score ${hasIncompleteMeals ? 'urgent-pulse-opacity' : ''}`}
          style={{ 
            fontSize: '0.8rem', 
            padding: '2px 8px', 
            borderRadius: '12px', 
            background: badgeBg,
            color: badgeColor,
            fontWeight: 500
          }}
        >
          {score == null ? 'Score unavailable' : `${formatNutritionNumber(score)}% on target`}
        </span>
      </div>
      <div style={{ fontSize: '0.8rem', color: '#9ca3af' }}>
        {/* Calories — full width on its own row */}
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
            <span>Cals</span>
            <span>
              {formatNutritionNumber(totals.calories)} / {formatNutritionNumber(calMin)} - {formatNutritionNumber(calMax)}
              {exceptionalBurnLabel}
            </span>
          </div>
          <div style={{ height: '6px', background: '#333', borderRadius: '3px', position: 'relative' }}>
            {/* Target Range Marker */}
            <div style={{
              position: 'absolute',
              left: `${minPct}%`,
              width: `${maxPct - minPct}%`,
              top: 0,
              bottom: 0,
              background: 'rgba(255, 255, 255, 0.1)',
              borderRadius: '2px' // Optional: slightly visual separate zone
            }} title="Target Range" />
            
            {/* Range Start/End ticks (optional, maybe too busy) */}
            
            {/* Progress Bar */}
            <div style={{ 
              position: 'absolute',
              left: 0,
              top: 0,
              bottom: 0,
              width: `${currentPct}%`, 
              background: barColor, 
              borderRadius: '3px',
              transition: 'all 0.3s ease-in-out'
            }} />
          </div>
        </div>

        {/* Protein + Water on a shared row, 50/50 */}
        {(() => {
          const waterTarget = targets.water_ml || 0;
          const waterTotal = Math.round(totals.water_ml || 0);
          // Hide the water bar on older days that have no logged water.
          // Water tracking is a recent feature, so a 0-bar on historical
          // days is just noise. Today and yesterday always show it (so the
          // user can still see/track an empty current day).
          const showWater = waterTarget > 0 && (isRecentDay || waterTotal > 0);
          const waterMet = waterTotal >= waterTarget;
          const waterVisualMax = Math.max(waterTarget / 0.85, waterTotal * 1.05);
          const waterTargetPct = waterTarget > 0
            ? Math.min(100, Math.max(0, (waterTarget / waterVisualMax) * 100))
            : 0;
          const waterCurrentPct = waterTarget > 0
            ? Math.min(100, Math.max(0, (waterTotal / waterVisualMax) * 100))
            : 0;
          const waterBarColor = waterMet ? '#10b981' : '#38bdf8';

          return (
            <div className="nutrition-secondary-bars" style={{ display: 'flex', gap: '15px', marginTop: '10px' }}>
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                  <span>Protein</span>
                  <span>{formatNutritionNumber(totals.protein)}g / {formatNutritionNumber(proMin)}g min</span>
                </div>
                <div style={{ height: '6px', background: '#333', borderRadius: '3px', position: 'relative' }}>
                  {/* Minimum Target Marker (vertical line) */}
                  <div style={{
                    position: 'absolute',
                    left: `${proMinPct}%`,
                    width: '2px',
                    top: '-2px',
                    bottom: '-2px',
                    background: 'rgba(255, 255, 255, 0.4)',
                    borderRadius: '1px'
                  }} title="Minimum Target" />
                  {/* Progress Bar */}
                  <div style={{
                    position: 'absolute',
                    left: 0,
                    top: 0,
                    bottom: 0,
                    width: `${proCurrentPct}%`,
                    background: proBarColor,
                    borderRadius: '3px',
                    transition: 'all 0.3s ease-in-out'
                  }} />
                </div>
              </div>
              {showWater && (
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                    <span>Water</span>
                    <span>{waterTotal} / {formatNutritionNumber(waterTarget)} ml min</span>
                  </div>
                  <div style={{ height: '6px', background: '#333', borderRadius: '3px', position: 'relative' }}>
                    {/* Minimum target marker */}
                    <div style={{
                      position: 'absolute',
                      left: `${waterTargetPct}%`,
                      width: '2px',
                      top: '-2px',
                      bottom: '-2px',
                      background: 'rgba(255, 255, 255, 0.4)',
                      borderRadius: '1px'
                    }} title="Minimum Target" />
                    {/* Progress */}
                    <div style={{
                      position: 'absolute',
                      left: 0,
                      top: 0,
                      bottom: 0,
                      width: `${waterCurrentPct}%`,
                      background: waterBarColor,
                      borderRadius: '3px',
                      transition: 'all 0.3s ease-in-out'
                    }} />
                  </div>
                </div>
              )}
            </div>
          );
        })()}
      </div>
      <div style={{ display: 'flex', gap: '10px', marginTop: '8px', fontSize: '0.75rem', color: '#666', justifyContent: 'center', flexWrap: 'wrap' }}>
        {Number.isFinite(totals.carbs) && <span>Carbs: {formatNutritionNumber(totals.carbs)}g{Number.isFinite(targets.carbs) ? ` / ${formatNutritionNumber(targets.carbs)}g` : ''}</span>}
        {Number.isFinite(totals.fat) && <span>Fat: {formatNutritionNumber(totals.fat)}g{Number.isFinite(targets.fat) ? ` / ${formatNutritionNumber(targets.fat)}g` : ''}</span>}
        {exceptionalBurnBonus > 0 && <span style={{ color: '#fbbf24' }}>Exceptional activity: +{Math.round(exceptionalBurnBonus)} kcal</span>}
      </div>
    </div>
  );
};

// Day-specific Recommendation Card - shows within each day section
export const DayRecommendationCard = ({ recommendation, coachHomePreview, coachHomePreviewSettled = true, coachDayKey, timeZone, isToday, isGenerating, dailyPending, isThinking, openRecommendations, aiCoachName, aiCoachEmoji, aiCoachImageUrl, hasAiFeature, openUpgrade, backendService, openCoachChat, onRegenerate }) => {
  const adviceText = recommendation?.data?.general_advice || recommendation?.general_advice || '';
  const coachMessage = isToday
    ? findLatestCoachMessage(coachHomePreview, recommendation, { settled: coachHomePreviewSettled, dayKey: coachDayKey || getCoachDayKey(Date.now(), timeZone), timeZone })
    : null;
  

  // Show locked state for users without AI feature (only on today's section)
  if (isToday && !hasAiFeature) {
    return (
      <div className="day-recommendation-card today locked" onClick={openUpgrade}>
        <div className="day-recommendation-content">
          <div className="day-recommendation-avatar">
            <CoachAvatar imageUrl={aiCoachImageUrl} emoji={aiCoachEmoji || '🥋'} identitySettled alt="Coach" eager />
          </div>
          {isToday && <strong className="day-recommendation-coach-name">{aiCoachName || 'Coach'}</strong>}
          <div className="day-recommendation-text">
            <p className="locked-text">
              <strong>🔒 AI Assistant</strong> — Get personalized training insights and recommendations
            </p>
          </div>
          <span className="upgrade-hint">Upgrade to Pro →</span>
        </div>
      </div>
    );
  }
  
  // Keep a stable identity and entry point even without a published message.
  // Real generation remains visible; initial context loading belongs in Hub.
  if (isToday && !coachMessage) {
    return (
      <div className={`day-recommendation-card today ${isGenerating ? 'generating' : 'loading-message'}`}>
        <CoachFeedLoadingSlot contextLoading aiCoachName={aiCoachName} aiCoachEmoji={aiCoachEmoji}
          aiCoachImageUrl={aiCoachImageUrl} hasAiFeature={hasAiFeature} openCoachChat={openCoachChat} dailyPending={isGenerating || dailyPending}>
          {isThinking && <CoachInlineTyping name={aiCoachName} />}
        </CoachFeedLoadingSlot>
      </div>
    );
  }

  // Historical cards remain day-specific; Today uses the canonical message.
  if (isToday ? !coachMessage : (!recommendation || !adviceText)) return null;

  const cardClass = `day-recommendation-card ${isToday ? 'today' : 'historical'}`;
  
  // Each day's insight is really the coach's message for that day, so tapping
  // it (or "View more") opens the chat focused on that specific insight rather
  // than a separate modal. Falls back to the modal if the coach chat isn't
  // available (e.g. no AI feature).
  const insightId = isToday ? coachMessage.focusInsightId : recommendation?._id;
  const openInsight = (hasAiFeature && openCoachChat)
    ? () => insightId ? openCoachChat({ focusInsightId: insightId }) : openCoachChat()
    : openRecommendations;

  return (
    <>
    <div className={cardClass} onClick={openInsight}>
      {/* Calorie-debt penalty banner — only on today's card. Sits above
          the avatar/insight so it's the first thing the user sees. */}
      {isToday && insightId && String(insightId) === String(recommendation?._id) && (
        <PenaltyCallout
          callout={recommendation?.data?.penalty_callout}
          onClick={openInsight}
        />
      )}
      <div className="day-recommendation-content">
        {/* AI Assistant Avatar */}
        <div className="day-recommendation-avatar">
          <CoachAvatar
            imageUrl={aiCoachImageUrl}
            emoji={aiCoachEmoji || '🥋'}
            identitySettled
            alt="AI Assistant"
            eager
            emojiClassName="coach-emoji"
          />
        </div>
        
        {isToday && <strong className="day-recommendation-coach-name">{aiCoachName || 'Coach'}</strong>}
        {/* Advice Text */}
        <div className="day-recommendation-text daily-insight-ready" ref={element => {
          // Exact identity only: the card proves the awaited insight when it
          // renders that recommendation, bound to its status context.
          if (element && recommendation?.status_context_id && String(insightId) === String(recommendation?._id)) {
            noteCoachOutputCommitted('readiness_review', String(recommendation.status_context_id), element, 'daily_card');
          }
        }}>
          {isToday ? (
            // Saved canonical content is already complete; never replay typing.
            coachMessage.paragraphs.map((paragraph, index) => (
              <CoachVisibleMessage as="div" key={`${coachMessage.id}-${index}`} insightId={insightId} text={paragraph}>
                <CoachMessageContent text={paragraph} />
              </CoachVisibleMessage>
            ))
          ) : (
            // Static text for historical days
            <CoachVisibleMessage insightId={insightId} text={adviceText} />
          )}
          
          {/* Click hint */}
          <span className="click-hint">View more →</span>
        </div>
      </div>
      {isToday && coachMessage.actions.length > 0 && <CoachActionCards actions={coachMessage.actions} backendService={backendService} />}
      {isToday && (isGenerating || dailyPending) && <DailyInsightCue />}
      {isToday && isThinking && <CoachInlineTyping name={aiCoachName} />}
    </div>

    {/* Separate "Chat with coach" entry point — only on today's insight,
        for users with the AI feature. Kept distinct from the insight card
        so the feed stays simple and the full conversation is the single
        place the user talks to the coach. */}
    {isToday && hasAiFeature && backendService && (
      <div className="day-recommendation-actions">
        <button
          type="button"
          className="day-recommendation-chat-btn"
          onClick={() => openCoachChat && openCoachChat()}
        >
          💬 Chat with {aiCoachName || 'your coach'}
        </button>
        {canRegenerateDailyInsight({ isToday, hasAiFeature, isGenerating }) && (
          <button
            type="button"
            className="day-recommendation-regenerate-btn"
            onClick={(event) => {
              event.stopPropagation();
              onRegenerate?.();
            }}
          >
            🔄 Regenerate insight
          </button>
        )}
      </div>
    )}
    </>
  );
};

const ActivityFeedLoadingSlot = () => (
  <div className="activity-slot-loading activity-feed-loading-slot" role="status" aria-label="Loading today’s activities">
    <div className="activity-skeleton activity-skeleton-section-title" />
    <div className="activity-slot-loading-row">
      <div className="activity-skeleton activity-skeleton-dot" />
      <div className="activity-skeleton activity-skeleton-activity-name" />
      <div className="activity-skeleton activity-skeleton-metric" />
      <div className="activity-skeleton activity-skeleton-time" />
    </div>
    <div className="activity-slot-loading-button">
      <div className="activity-skeleton activity-skeleton-primary-button" />
    </div>
  </div>
);

const ActivityDataLoadError = ({ onRetry }) => (
  <div className="activity-slot-error" role="alert">
    <span>We couldn’t load today’s activities.</span>
    <button type="button" className="activity-data-retry" onClick={onRetry}>
      Retry
    </button>
  </div>
);

export const TodayCoachSection = ({ headingRef, statusControl, children }) => (
  <>
    <p id="today-activities" ref={headingRef} tabIndex={-1} className="activity-date today-coach-heading">Today</p>
    {statusControl}
    {children}
  </>
);

export const ActivitiesGlobalFeedHeader = ({ filterControl, headingRef, statusControl, children }) => (
  <>
    {filterControl && <div className="activities-filter-indicator">
      {filterControl}
    </div>}
    <TodayCoachSection headingRef={headingRef} statusControl={statusControl}>
      {children}
    </TodayCoachSection>
  </>
);

// Canonical daily read: the decoded body is attributed only to the open flow of
// the exact status context it names; pending/empty/old-status bodies never
// stand in for the awaited insight.
export const readCanonicalDailyRecommendation = backendService => {
  const startedAt = coachSequenceNow();
  return backendService.fetchRecommendations().then(recommendation => {
    noteCoachCanonicalRead('readiness_review', recommendation?.status_context_id == null ? null : String(recommendation.status_context_id), startedAt);
    return recommendation;
  });
};

const Activities = ({ onActivityComplete, modalService, backendService, onOpenPlanEditor, badgeLoaded, onWorkoutStatusChange, dojoChangeVersion, onDojoChange, todayIdentity, activityShortcutTarget, activityFilterRef, onActivityFilterSummary, backgroundImageUrl, backgroundFocalPoint }) => {
  const localFilterRef = useRef(null);
  const filterControlRef = activityFilterRef || localFilterRef;
  const [filterSummary, setFilterSummary] = useState({ active: false, label: 'Displaying all activities' });
  useEffect(() => {
    onActivityFilterSummary?.(filterSummary.label);
  }, [filterSummary.label, onActivityFilterSummary]);
  const [exercises, setExercises] = useState([]);
  const [activities, setActivities] = useState([]);
  const [cancelledShortcutWorkoutId, setCancelledShortcutWorkoutId] = useState(null);
  const [primaryActivitiesSettled, setPrimaryActivitiesSettled] = useState(false);
  const startupRequestPlan = getActivityStartupRequestPlan({ primaryActivitiesSettled });
  const secondaryHydrationRef = useRef(startupRequestPlan.secondaryHydration);
  secondaryHydrationRef.current = startupRequestPlan.secondaryHydration;
  const [travelReplacementAvailability, setTravelReplacementAvailability] = useState(null);
  const travelReplacementEnabled = isTravelReplacementEnabledForService(
    travelReplacementAvailability,
    backendService
  );
  const travelAllowanceRequestRef = useRef(0);
  const refreshTravelReplacementAvailability = useCallback(async () => {
    const requestId = ++travelAllowanceRequestRef.current;
    try {
      const allowance = await backendService.getTravelReplacementAllowance();
      if (requestId === travelAllowanceRequestRef.current) {
        setTravelReplacementAvailability({
          backendService,
          enabled: isTravelReplacementAvailable(allowance),
        });
      }
    } catch (_) {
      if (requestId === travelAllowanceRequestRef.current) {
        setTravelReplacementAvailability({ backendService, enabled: false });
      }
    }
  }, [backendService]);

  useEffect(() => {
    if (!startupRequestPlan.secondaryHydration) return undefined;
    setTravelReplacementAvailability(null);
    refreshTravelReplacementAvailability();
    const handleAllowanceChanged = () => refreshTravelReplacementAvailability();
    window.addEventListener('regimen:travelReplacements:changed', handleAllowanceChanged);
    return () => {
      travelAllowanceRequestRef.current += 1;
      window.removeEventListener('regimen:travelReplacements:changed', handleAllowanceChanged);
    };
  }, [refreshTravelReplacementAvailability, startupRequestPlan.secondaryHydration]);
  const [expiryRevision, setExpiryRevision] = useState(() => Date.now());
  useForegroundInterval(() => setExpiryRevision(Date.now()), 60 * 1000);
  // Exactly one reorder workflow may own the feed at a time. This prevents a
  // second activity mutation from racing the optimistic drag state.
  const [activityInteractionLock, setActivityInteractionLock] = useState(null);
  const handleWorkoutReorderModeChange = useCallback((activityId, active) => {
    setActivityInteractionLock(current => active
      ? { kind: 'workout-reorder', activityId: String(activityId) }
      : (current?.kind === 'workout-reorder' && String(current.activityId) === String(activityId) ? null : current));
  }, []);
  const handleFoodMoveModeChange = useCallback((dayKey, active) => {
    setActivityInteractionLock(current => active
      ? { kind: 'food-move', dayKey }
      : (current?.kind === 'food-move' && current.dayKey === dayKey ? null : current));
  }, []);
  const cachedProfile = getCachedActivityProfile(backendService);
  const [userProfile, setUserProfile] = useState(() => cachedProfile);
  const workoutStartScope = useRef(null);
  const workoutStartOwner = userProfile?._id || userProfile?.id || userProfile?.user_id;
  if (!workoutStartScope.current || workoutStartScope.current.service !== backendService
    || workoutStartScope.current.identity !== backendService.authenticatedIdentity
    || workoutStartScope.current.owner !== workoutStartOwner
    || workoutStartScope.current.positionOwner !== backendService.positionOwner) {
    workoutStartScope.current?.state.dispose();
    workoutStartScope.current = { service: backendService, identity: backendService.authenticatedIdentity,
      owner: workoutStartOwner, positionOwner: backendService.positionOwner, state: createWorkoutStartReconciliation() };
  }
  const workoutStarts = workoutStartScope.current.state;
  useLayoutEffect(() => {
    workoutStarts.activate();
    return () => workoutStarts.dispose();
  }, [workoutStarts]);
  const [profileSettled, setProfileSettled] = useState(() => Boolean(cachedProfile));
  const userProfileRef = useRef(cachedProfile);
  const profileRequestRef = useRef(0);
  const [askCoachTyping, setAskCoachTyping] = useState(() => getCoachAskTypingSnapshot());
  const [coachHubOpen, setCoachHubOpen] = useState(() => getCoachHubOpenSnapshot());
  const coachHubOpenRef = useRef(coachHubOpen);
  const [coachReactionToast, setCoachReactionToast] = useState(() => getCoachReactionToastSnapshot());
  const [coachReviewTypingEventIds, setCoachReviewTypingEventIds] = useState(() => getCoachReactionTypingEventIdsSnapshot());
  const coachReviewTypingEventIdsRef = useRef(coachReviewTypingEventIds);
  useEffect(() => subscribeCoachAskTyping(setAskCoachTyping), []);
  useEffect(() => subscribeCoachHubOpen(open => {
    coachHubOpenRef.current = open;
    setCoachHubOpen(open);
  }), []);
  useEffect(() => subscribeCoachReactionToast(setCoachReactionToast), []);
  useEffect(() => subscribeCoachReactionTypingEvents(eventIds => {
    const normalized = (eventIds || []).map(String);
    if (normalized.join('|') === coachReviewTypingEventIdsRef.current.join('|')) return;
    coachReviewTypingEventIdsRef.current = normalized;
    coachPresenceSignalRef.current += 1;
    setCoachReviewTypingEventIds(normalized);
  }), []);
  useEffect(() => {
    if (!coachReactionToast?.eventId) return undefined;
    const timer = window.setTimeout(() => clearCoachReactionToast(coachReactionToast.eventId), 8000);
    return () => window.clearTimeout(timer);
  }, [coachReactionToast]);
  
  // Subscription feature check for AI recommendations
  const { hasAccess: hasAiFeature, openUpgrade } = useFeature('ai_recommendations');
  
  // Dojo feed data for ambient DojoPulse cards in personal feed
  const [dojoFeedData, setDojoFeedData] = useState({ users: [], activities: [], coachComments: [], dojoName: '', dojoId: '', inDojo: null });
  const [socialPresenceByDate, setSocialPresenceByDate] = useState({});
  const [presenceRevision, setPresenceRevision] = useState(0);
  
  // Fetch dojo feed for DojoPulse cards. Keep the last known membership while
  // refreshing on resume; a transient empty response must not render the
  // not-in-dojo nudge for a member who is still in the dojo.
  const [dojoStatusLoaded, setDojoStatusLoaded] = useState(false);
  const dojoFeedDataRef = useRef(dojoFeedData);
  const dojoMembershipRequestRef = useRef(0);
  const dojoMembershipOutcomeRef = useRef(Promise.resolve(false));
  const dojoSocialFeedRequestRef = useRef(0);
  const fetchDojoFeed = useCallback(async ({ includeMembership = true, includeFeed = true } = {}) => {
    const membershipRequestId = includeMembership ? ++dojoMembershipRequestRef.current : null;
    const socialFeedRequestId = includeFeed ? ++dojoSocialFeedRequestRef.current : null;
    const requestStartedAt = new Date();
    const { membership, feed } = loadDojoFeedProgressively(backendService, {
      includeMembership,
      includeFeed,
    });

    // Membership is the only information needed to choose the correct Coach
    // shell. Resolve it through the lightweight dojo endpoint instead of
    // blocking the visible message on the much heavier social feed payload.
    let membershipOutcome = dojoMembershipOutcomeRef.current;
    if (includeMembership) {
      membershipOutcome = membership.then(dojo => {
        if (membershipRequestId !== dojoMembershipRequestRef.current
          || dojo === undefined) return false;
        const next = mergeDojoMembershipContext(dojoFeedDataRef.current, dojo);
        dojoFeedDataRef.current = next;
        setDojoFeedData(next);
        setDojoStatusLoaded(true);
        return true;
      }).catch(() => {
        if (membershipRequestId === dojoMembershipRequestRef.current
          && dojoFeedDataRef.current.inDojo !== null) {
          setDojoStatusLoaded(true);
        }
        return false;
      });
      dojoMembershipOutcomeRef.current = membershipOutcome;
    }

    if (!includeFeed) {
      await membershipOutcome;
      if (includeMembership && membershipRequestId === dojoMembershipRequestRef.current) {
        setDojoStatusLoaded(true);
      }
      return;
    }

    try {
      const data = await feed;
      // If this refresh includes membership, wait for that smaller request to
      // own identity. Deferred startup hydration reuses the already-resolving
      // membership request instead of invalidating and refetching it.
      const membershipAuthoritative = await membershipOutcome;
      // A resume refresh can overlap the initial request. Never let an older
      // social response overwrite newer social data.
      if (socialFeedRequestId !== dojoSocialFeedRequestRef.current) return;
      if (data && Array.isArray(data.activities) && typeof data.inDojo === 'boolean') {
        const hydratedData = mergeDojoFeedHydration(
          data,
          dojoFeedDataRef.current,
          membershipAuthoritative,
        );
        const next = {
          users: hydratedData.users || [],
          activities: hydratedData.activities,
          coachComments: mergeHydratedRosterComments(
            hydratedData.coachComments || [],
            dojoFeedDataRef.current.coachComments || [],
            requestStartedAt,
            hydratedData.dojoId || ''
          ),
          dojoName: hydratedData.dojoName || '',
          dojoId: hydratedData.dojoId || '',
          inDojo: hydratedData.inDojo,
        };
        dojoFeedDataRef.current = next;
        setDojoFeedData(next);
      }
    } catch (e) {
      const membershipAuthoritative = includeMembership ? await membershipOutcome : false;
      if (socialFeedRequestId !== dojoSocialFeedRequestRef.current) return;
      // Preserve an authoritative or previously known dojo context on
      // resume/network failure; otherwise keep membership unknown.
      if (!membershipAuthoritative && !dojoFeedDataRef.current.dojoId) {
        dojoFeedDataRef.current = { users: [], activities: [], coachComments: [], dojoName: '', dojoId: '', inDojo: null };
      }
    } finally {
      // Do not settle the Coach slot from a feed-first response. The small
      // membership request owns shell selection whenever it is part of this refresh.
      await membershipOutcome;
      if (includeMembership && membershipRequestId === dojoMembershipRequestRef.current) {
        setDojoStatusLoaded(true);
      }
    }
  }, [backendService]);

  useEffect(() => {
    setDojoStatusLoaded(false);
    fetchDojoFeed({ includeFeed: false });
  }, [fetchDojoFeed, dojoChangeVersion]);

  useEffect(() => {
    if (!startupRequestPlan.secondaryHydration) return;
    fetchDojoFeed({ includeMembership: false });
  }, [fetchDojoFeed, dojoChangeVersion, startupRequestPlan.secondaryHydration]);

  // Resume-triggered refresh is wired through the foreground subscription
  // below so it does not run a timer while backgrounded.
  useEffect(() => subscribeForeground((active) => {
    if (active) fetchDojoFeed({ includeFeed: secondaryHydrationRef.current });
  }), [fetchDojoFeed]);

  // Fetch the compact, privacy-filtered social context for each visible day.
  useEffect(() => {
    if (!startupRequestPlan.secondaryHydration) return undefined;
    let cancelled = false;
    const fetchSocialPresence = async () => {
      try {
        const data = await backendService.getSocialPresence(null, null, 100);
        if (cancelled) return;
        const byDate = {};
        for (const [key, audience] of [['friends', data?.friends], ['dojo', data?.dojo]]) {
          for (const day of audience?.people || []) {
            if (!byDate[day.dateKey]) byDate[day.dateKey] = { friends: [], dojo: [] };
            byDate[day.dateKey][key] = day.people || [];
          }
        }
        setSocialPresenceByDate(byDate);
      } catch (error) {
        if (!cancelled) setSocialPresenceByDate({});
        console.debug('Social presence unavailable:', error.message);
      }
    };
    fetchSocialPresence();
    return () => { cancelled = true; };
  }, [backendService, startupRequestPlan.secondaryHydration]);

  // Toast notifications for dojo activity updates
  const {pills: dojoPills, show: showDojoPill} = useDojoToasts(userProfile);
  const [coachReadyToast, setCoachReadyToast] = useState(null);
  const [coachReviewToasts, setCoachReviewToasts] = useState([]);
  const coachReviewToastTimersRef = useRef(new Map());
  const coachReadyToastTimerRef = useRef(null);
  const lastCoachReadyKeyRef = useRef(null);
  const coachReadyResolvingKeysRef = useRef(new Set());
  const resolvedCoachReviewEventIdsRef = useRef(new Set());
  const showCoachReadyToast = useCallback((key, recommendationOrAnswer, resolvedCoachName = null) => {
    const answer = typeof recommendationOrAnswer === 'string'
      ? recommendationOrAnswer.trim().slice(0, 800)
      : coachAnswerFromRecommendation(recommendationOrAnswer);
    if (!key || !answer || lastCoachReadyKeyRef.current === key) return;
    lastCoachReadyKeyRef.current = key;
    if (coachReadyToastTimerRef.current) clearTimeout(coachReadyToastTimerRef.current);
    setCoachReadyToast({
      content: typeof recommendationOrAnswer === 'string' ? recommendationOrAnswer.trim() : coachAnswerFromRecommendation(recommendationOrAnswer, Infinity),
      key,
      answer,
      coachName: resolvedCoachName,
      focusTimelineEntryId: coachReadyToastTargetId(key, recommendationOrAnswer),
      typing: false,
      exiting: false,
    });
    coachReadyToastTimerRef.current = setTimeout(() => {
      setCoachReadyToast(current => current?.key === key ? { ...current, exiting: true } : current);
      coachReadyToastTimerRef.current = setTimeout(() => {
        setCoachReadyToast(current => current?.key === key ? null : current);
      }, 500);
    }, 8000);
  }, []);
  const scheduleCoachReviewToastExpiry = useCallback((eventId) => {
    const key = coachReviewToastKey(eventId);
    if (!key) return;
    const existing = coachReviewToastTimersRef.current.get(key);
    if (existing) clearTimeout(existing);
    const timer = setTimeout(() => {
      setCoachReviewToasts(current => markCoachReviewToastExiting(current, eventId));
      const exitTimer = setTimeout(() => {
        setCoachReviewToasts(current => removeCoachReviewToast(current, eventId));
        coachReviewToastTimersRef.current.delete(key);
      }, 500);
      coachReviewToastTimersRef.current.set(key, exitTimer);
    }, 8000);
    coachReviewToastTimersRef.current.set(key, timer);
  }, []);
  const rememberResolvedCoachReview = useCallback((eventId) => {
    const normalized = String(eventId || '').trim();
    if (!normalized) return;
    resolvedCoachReviewEventIdsRef.current.add(normalized);
    while (resolvedCoachReviewEventIdsRef.current.size > 500) {
      resolvedCoachReviewEventIdsRef.current.delete(resolvedCoachReviewEventIdsRef.current.values().next().value);
    }
  }, []);
  const resolveCoachReviewToast = useCallback(async (eventId, insightId) => {
    const normalizedEventId = String(eventId || '').trim();
    const key = coachReviewToastKey(normalizedEventId);
    if (!key || !insightId || resolvedCoachReviewEventIdsRef.current.has(normalizedEventId) || coachReadyResolvingKeysRef.current.has(key)) return;
    if (coachHubOpenRef.current) {
      rememberResolvedCoachReview(normalizedEventId);
      setCoachReviewToasts(current => removeCoachReviewToast(current, normalizedEventId));
      return;
    }
    coachReadyResolvingKeysRef.current.add(key);
    try {
      const readStartedAt = coachSequenceNow();
      const payload = await backendService.fetchCoachHome();
      noteCoachCanonicalRead('activity_review', normalizedEventId, readStartedAt);
      const answer = findCoachAnswerInHome(payload, insightId, Infinity);
      if (answer) noteCoachContextMatch('activity_review', normalizedEventId);
      if (coachHubOpenRef.current) {
        rememberResolvedCoachReview(normalizedEventId);
        setCoachReviewToasts(current => removeCoachReviewToast(current, normalizedEventId));
        return;
      }
      rememberResolvedCoachReview(normalizedEventId);
      if (answer) {
        setCoachReviewToasts(current => completeCoachReviewToast(current, {
          eventId: normalizedEventId,
          insightId,
          answer,
          coachName: payload?.coach?.name || null,
        }));
        scheduleCoachReviewToastExpiry(normalizedEventId);
      } else {
        setCoachReviewToasts(current => removeCoachReviewToast(current, normalizedEventId));
      }
    } catch (resolveError) {
      console.error('[CoachToast] failed to resolve generated answer', resolveError);
      setCoachReviewToasts(current => removeCoachReviewToast(current, normalizedEventId));
    } finally {
      coachReadyResolvingKeysRef.current.delete(key);
    }
  }, [backendService, rememberResolvedCoachReview, scheduleCoachReviewToastExpiry]);
  useEffect(() => {
    if (!coachHubOpen) return;
    coachReviewToastTimersRef.current.forEach(timer => clearTimeout(timer));
    coachReviewToastTimersRef.current.clear();
    setCoachReviewToasts([]);
  }, [coachHubOpen]);
  useEffect(() => {
    if (coachHubOpen) return;
    setCoachReviewToasts(current => coachReviewTypingEventIds.reduce((next, eventId) => (
      startCoachReviewToast(next, {
        eventId,
        coachName: userProfile?.ai_coach_settings?.name || null,
        hubOpen: coachHubOpen,
      })
    ), current));
  }, [coachHubOpen, coachReviewTypingEventIds, userProfile?.ai_coach_settings?.name]);
  useEffect(() => () => {
    if (coachReadyToastTimerRef.current) clearTimeout(coachReadyToastTimerRef.current);
    coachReviewToastTimersRef.current.forEach(timer => clearTimeout(timer));
    coachReviewToastTimersRef.current.clear();
  }, []);
  const effectiveTimezone = useMemo(
    () => userProfile?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
    [userProfile?.timezone]
  );

  const getDateKeyForUser = useCallback((date) => getDateKeyInTimezone(date, effectiveTimezone), [effectiveTimezone]);

  const mergeSocialPresenceEvent = useCallback((msg) => {
    setSocialPresenceByDate(prev => mergeSocialPresenceByDate(prev, msg, getDateKeyForUser));
  }, [getDateKeyForUser]);

  const formatUserDate = useCallback(
    (date, locale = undefined, options = {}) => formatDateInTimezone(date, effectiveTimezone, locale, options),
    [effectiveTimezone]
  );

  // Handlers for DojoPulse interactions
  const handleDojoUserClick = useCallback((user) => {
    if (user?._id) {
      modalService.open(SocialProfileModal, {
        userId: user._id,
        userName: user.display_name || user.email?.split('@')[0],
        backendService
      });
    }
  }, [modalService, backendService]);

  const handleDojoActivityClick = useCallback((activity, user) => {
    if (activity?._id) {
      const currentUserId = userProfile?._id || userProfile?.id || userProfile?.user_id;
      const targetUserId = user?._id || user?.user_id || user?.id;
      const isSelf = Boolean(targetUserId && currentUserId && String(targetUserId) === String(currentUserId));
      modalService.open(ActivityDetailModal, {
        activityId: activity._id,
        user,
        isSelf,
        backendService,
        exercises
      });
    }
  }, [modalService, backendService, exercises, userProfile?._id, userProfile?.id, userProfile?.user_id]);

  const handleSocialPresenceOpen = useCallback((audience, people, label) => {
    modalService.open(SocialPresenceModal, {
      audience,
      initialPeople: people,
      initialLabel: label,
      backendService,
      onUserClick: handleDojoUserClick,
      onActivityClick: handleDojoActivityClick,
    });
  }, [modalService, backendService, handleDojoUserClick, handleDojoActivityClick]);

  // Open the standalone dojo from the feed nudge.
  const openDojoTab = useCallback(() => {
    modalService.open(DojoHomeModal, {
      backendService,
      onDojoChange
    });
  }, [modalService, backendService, onDojoChange]);

  // User status (how they feel today)
  const [currentStatus, setCurrentStatus] = useState(null);
  const currentStatusContextIdRef = useRef(null);
  const currentStatusContextCreatedAtRef = useRef(null);
  const [isSubmittingStatus, setIsSubmittingStatus] = useState(false);
  // Bumped when user status changes, so open Workout components refetch exercise recommendations
  const [recommendationVersion, setRecommendationVersion] = useState(0);
  // Per-day statuses from backend API
  const [dayStatuses, setDayStatuses] = useState({});

  // Fetch latest status on mount (for today's dropdown initial value)
  useEffect(() => {
    const fetchStatus = async () => {
      try {
        const latest = await backendService.getLatestStatus();
        if (latest?.is_today === true) {
          const incomingStatusContext = {
            id: latest._id ? String(latest._id) : null,
            createdAt: latest.created_at || null,
          };
          const currentStatusContext = currentStatusContextIdRef.current ? {
            id: currentStatusContextIdRef.current,
            createdAt: currentStatusContextCreatedAtRef.current,
          } : null;
          if (!shouldAcceptStatusContext(currentStatusContext, incomingStatusContext)) return;
          currentStatusContextIdRef.current = incomingStatusContext.id;
          currentStatusContextCreatedAtRef.current = incomingStatusContext.createdAt;
          setCurrentStatus(latest.data.status);
        }
      } catch (e) {
        console.error('Failed to fetch latest status:', e);
      }
    };
    fetchStatus();
    let active = true;
    const handleDailyStatusSaved = event => {
      const status = event.detail?.status || null;
      const statusContextId = event.detail?.statusContextId
        ? String(event.detail.statusContextId)
        : null;
      const statusCreatedAt = event.detail?.statusCreatedAt || null;
      if (!shouldAcceptStatusContext(
        currentStatusContextIdRef.current ? {
          id: currentStatusContextIdRef.current,
          createdAt: currentStatusContextCreatedAtRef.current,
        } : null,
        { id: statusContextId, createdAt: statusCreatedAt }
      )) return;
      currentStatusContextIdRef.current = statusContextId;
      currentStatusContextCreatedAtRef.current = statusCreatedAt;
      const today = getDateKeyForUser(new Date());
      setCurrentStatus(status);
      setDayStatuses(prev => ({ ...prev, [today]: status }));
      setAwaitingStatus(false);
      setLatestRecommendation(null);
      setRecommendationsByDate(prev => {
        const next = { ...prev };
        delete next[today];
        return next;
      });
      if (!shouldPollHostedDailyInsight(event.detail)) {
        setIsGeneratingRec(false);
        isGeneratingRecRef.current = false;
        return;
      }
      setIsGeneratingRec(true);
      isGeneratingRecRef.current = true;

      const polledStatusContextId = currentStatusContextIdRef.current;
      pollForStatusInformedInsight({
        fetchRecommendation: () => readCanonicalDailyRecommendation(backendService),
        isDailyInsight: recommendation => (
          isDailyFeedRecommendation(recommendation)
          && isRecommendationForStatusContext(recommendation, currentStatusContextIdRef.current)
        ),
        shouldContinue: () => active && isGeneratingRecRef.current && currentStatusContextIdRef.current === polledStatusContextId,
        maxAttempts: 24,
        intervalMs: 5000,
      }).then(recommendation => {
        if (!active || currentStatusContextIdRef.current !== polledStatusContextId) return;
        setIsGeneratingRec(false);
        isGeneratingRecRef.current = false;
        if (!recommendation) return;
        noteCoachContextMatch('readiness_review', String(recommendation.status_context_id || ''));
        const recDate = recommendation.target_date || today;
        setLatestRecommendation(recommendation);
        setRecommendationsByDate(prev => ({ ...prev, [recDate]: recommendation }));
      }).catch(error => {
        if (active && currentStatusContextIdRef.current === polledStatusContextId) {
          setIsGeneratingRec(false);
          isGeneratingRecRef.current = false;
          console.error('Status-informed Daily Insight polling failed:', error);
        }
      });
    };
    window.addEventListener('daily-status-saved', handleDailyStatusSaved);
    return () => {
      active = false;
      window.removeEventListener('daily-status-saved', handleDailyStatusSaved);
    };
  }, [backendService, getDateKeyForUser]);

  const handleStatusChange = useCallback(async (newStatus, note = null) => {
    if (newStatus === currentStatus && !note) return true;
    setIsSubmittingStatus(true);
    const coachFlow = beginCoachFlow('readiness_review', typeof window !== 'undefined' ? window.event?.timeStamp : undefined);
    try {
      markCoachFlow(coachFlow, 'save_start');
      const savedStatus = await backendService.setUserStatus(newStatus, note, 'dropdown').catch(error => { failCoachFlow(coachFlow); throw error; });
      markCoachFlow(coachFlow, 'save_end');
      bindCoachFlowSubject(coachFlow, String(savedStatus?._id || ''));
      setCurrentStatus(newStatus);
      window.dispatchEvent(new CustomEvent('daily-status-saved', {
        detail: {
          status: newStatus,
          statusContextId: savedStatus?._id || null,
          statusCreatedAt: savedStatus?.created_at || null,
          externalDailyTaskId: savedStatus?.external_daily_task_id || null,
          externalDailyFailureCode: savedStatus?.external_daily_failure_code || null,
          externalDailyCapturePending: savedStatus?.external_daily_capture_pending === true,
        },
      }));
      return true;
    } catch (e) {
      console.error('Failed to set status:', e);
      return { ok: false, message: statusCheckInErrorMessage(e, 'dropdown') };
    } finally {
      setIsSubmittingStatus(false);
    }
  }, [currentStatus, backendService]);

  const [showScrollToTop, setShowScrollToTop] = useState(false);
  const [hasTodayInFeed, setHasTodayInFeed] = useState(false);
  const firstDayGroupRef = useRef(null);
  const dayGroupRefs = useRef(new Map());
  const visibleDaysMap = useRef(new Map());
  
  const nextOffsetRef = useRef(0);
  const nextCursorRef = useRef(null);
  const oldestConsumedCreatedAtRef = useRef(null);
  const isLoadingRef = useRef(false);
  const activityRequestIdRef = useRef(0);
  const activityFilterVersionRef = useRef(0);
  const activitiesPerPage = 10;
  const [hasMore, setHasMore] = useState(true);
  // react-infinite-scroll-component only releases its internal request latch
  // when dataLength changes. Activity count is not a safe signal because a
  // fetched page can contain only already-unioned/deduplicated activities.
  // Advance this revision whenever a request settles so the loader cannot get
  // stuck forever after a busy or duplicate-only page.
  const [activityPaginationRevision, setActivityPaginationRevision] = useState(0);
  const hasMoreRef = useRef(true);
  const [oldestPersonalSourceDate, setOldestPersonalSourceDate] = useState(null);
  const [videoScope, setVideoScope] = useState('mine');
  const cameraMedia = useCameraMediaFeed(backendService, videoScope, videoScope === 'mine' && hasMore ? oldestPersonalSourceDate : null);
  const activityCountRef = useRef(0);
  const scrollRestoreStartedRef = useRef(false);
  hasMoreRef.current = hasMore;
  activityCountRef.current = activities.length;
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);
  const [activityLoadError, setActivityLoadError] = useState(false);
  const [hasAnyActivities, setHasAnyActivities] = useState(null);
  const [filters, setFilters] = useState({
    startDate: null,
    endDate: null,
    types: { workout: false, meal: false, media: false, metric: false, survey: false },
    statuses: { complete: true, pending: true, ongoing: true, expired: true },
  });
  const activityFilterVersion = activityFilterVersionRef.current;
  const [latestRecommendation, setLatestRecommendation] = useState(null);
  const [isGeneratingRec, setIsGeneratingRec] = useState(false);
  // Activity-triggered coach updates generate asynchronously and do not replace
  // the daily insight, so they are tracked separately but still feed the single
  // unified "coach is thinking" presence signal shared with the chat modal.
  const [activityUpdateInFlight, setActivityUpdateInFlight] = useState(false);
  const [coachActivityEvents, setCoachActivityEvents] = useState([]);
  const { pending: dailyCoachPending, failed: dailyCoachFailed, status: dailyCoachStatus, retry: retryDailyCoach,
    retrying: dailyCoachRetrying, retryError: dailyCoachRetryError, retryUncertain: dailyCoachRetryUncertain } = useDailyCoachTaskStatus(backendService);
  const { coachHomePreview, coachHomePreviewSettled, coachDayKey } = useCoachHomePreview({
    backendService, hasAiFeature, enabled: profileSettled, timeZone: effectiveTimezone,
  });
  // The consumed receipt means publication is complete. Refresh the canonical
  // Coach Home source rather than rendering receipt prose over the existing card.
  useEffect(() => {
    if (dailyCoachStatus?.external_daily_task?.status === 'consumed'
        && dailyCoachStatus?.external_daily_task?.insight?.status_context_id === String(dailyCoachStatus?._id)) {
      window.dispatchEvent(new Event('coach-timeline-updated'));
    }
  }, [dailyCoachStatus?._id, dailyCoachStatus?.external_daily_task?.status,
    dailyCoachStatus?.external_daily_task?.insight?.status_context_id]);
  // Recommendations indexed by date (YYYY-MM-DD)
  const [recommendationsByDate, setRecommendationsByDate] = useState({});
  const fetchedRecDatesRef = useRef(new Set()); // Track fetched dates to avoid re-fetching
  const [hasWorkoutPlan, setHasWorkoutPlan] = useState(true); // Assume true initially to avoid flash
  const isGeneratingRecRef = useRef(false);
  const recommendationPollTimeoutRef = useRef(null);
  const coachPresenceRequestRef = useRef(0);
  const coachPresenceSignalRef = useRef(0);
  const coachActivityEventsRef = useRef([]);

  const refreshCoachPresenceEvents = useCallback(async () => {
    if (!secondaryHydrationRef.current) return [];
    const requestId = ++coachPresenceRequestRef.current;
    const signalVersion = coachPresenceSignalRef.current;
    if (!hasAiFeature || !backendService?.fetchCoachActivityEvents) {
      coachActivityEventsRef.current = [];
      setCoachActivityEvents([]);
      return [];
    }
    try {
      const payload = await backendService.fetchCoachActivityEvents();
      const events = Array.isArray(payload?.events) ? payload.events : [];
      if (requestId === coachPresenceRequestRef.current) {
        // Canonical state repairs a missed terminal WebSocket event (for example
        // during reconnect/deploy). Without this, the local in-flight flag can
        // leave the Warden "reviewing…" chip visible forever even though the
        // server has no pending or claimed Coach event.
        // Do not let an HTTP snapshot taken before a newer WebSocket signal
        // clear legitimate generation that started while this request was in
        // flight. The next canonical refresh will reconcile it.
        if (canApplyCanonicalPresenceSnapshot(signalVersion, coachPresenceSignalRef.current)) {
          const generatedEvents = findNewlyGeneratedCoachEvents(coachActivityEventsRef.current, events);
          coachActivityEventsRef.current = events;
          setCoachActivityEvents(events);
          setActivityUpdateInFlight(current => reconcileActivityReviewPresence(
            current,
            events,
            payload?.hasAwaitingActivityReview,
          ));
          const canonicalTypingEventIds = events
            .filter(event => event?.generation_state === 'claimed' && event?.reply_worthwhile !== false)
            .map(event => String(event._id));
          reconcileCoachReactionTypingEventIds(canonicalTypingEventIds);
          generatedEvents.forEach(generated => {
            resolveCoachReviewToast(generated._id, generated.insight_id);
          });
          const canonicalTypingSet = new Set(canonicalTypingEventIds);
          const canonicalFailures = new Map(events
            .filter(event => event?.generation_state === 'failed')
            .map(event => [String(event._id), event]));
          setCoachReviewToasts(current => current.flatMap(toast => {
            if (!toast.typing) return [toast];
            const eventId = String(toast.eventId);
            if (canonicalTypingSet.has(eventId) || coachReadyResolvingKeysRef.current.has(toast.key)) return [toast];
            const failed = canonicalFailures.get(eventId);
            if (!failed) return [];
            return failCoachReviewToast([toast], {
              eventId,
              coachName: toast.coachName || userProfileRef.current?.ai_coach_settings?.name || null,
              activityName: failed.activity_name,
              activityType: failed.activity_type,
              errorCode: failed.last_error_code,
            });
          }));
        }
      }
      return events;
    } catch (presenceError) {
      if (requestId === coachPresenceRequestRef.current) {
        console.error('[CoachPresence] canonical event refresh failed', presenceError);
      }
      return [];
    }
  }, [backendService, hasAiFeature, resolveCoachReviewToast]);

  useEffect(() => {
    const refreshWhenVisible = () => {
      if (typeof document === 'undefined' || document.visibilityState !== 'hidden') {
        refreshCoachPresenceEvents();
      }
    };
    refreshWhenVisible();
    const intervalId = window.setInterval(refreshWhenVisible, 30000);
    return () => window.clearInterval(intervalId);
  }, [refreshCoachPresenceEvents]);

  useEffect(() => {
    if (!startupRequestPlan.secondaryHydration) return;
    refreshCoachPresenceEvents();
  }, [refreshCoachPresenceEvents, startupRequestPlan.secondaryHydration]);

  // ── Unified coach presence ──
  // Feed cue includes generation, review and ask sources. Toast takeover is
  // reversible and depends on the cue's viewport visibility, not card mounting.
  const coachIsThinking = deriveCoachThinking({
    isGeneratingRec,
    activityUpdateInFlight,
    activityEvents: coachActivityEvents,
  });
  const coachThinkingName = userProfile?.ai_coach_settings?.name;
  const unscopedActivityReviewPending = shouldShowUnscopedActivityReviewToast(activityUpdateInFlight, coachReviewTypingEventIds);
  const dailyInsightPending = isGeneratingRec || dailyCoachPending;
  const unrelatedCoachToastPending = (unscopedActivityReviewPending && !coachHubOpen) || (askCoachTyping && !coachHubOpen);
  const inlineCoachTyping = deriveCoachThinking({ activityUpdateInFlight, activityEvents: coachActivityEvents }) || askCoachTyping || coachReviewTypingEventIds.length > 0;
  useEffect(() => {
    publishCoachThinking(coachIsThinking);
  }, [coachIsThinking]);
  useEffect(() => () => publishCoachThinking(false), []);

  // Track ongoing workout status and notify parent
  useEffect(() => {
    if (onWorkoutStatusChange) {
      const hasOngoing = activities.some(
        a => a.type === 'workout' && ONGOING_STATUSES.includes(a.status)
      );
      onWorkoutStatusChange(hasOngoing);
    }
  }, [activities, onWorkoutStatusChange]);

  // The Today button is only worth offering once the first day section has
  // scrolled above the viewport.
  useEffect(() => {
    if (!firstDayGroupRef.current) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          setShowScrollToTop(!entry.isIntersecting && entry.boundingClientRect.bottom < 0);
        });
      },
      {
        root: null,
        rootMargin: '-100px 0px 0px 0px', // Trigger a bit before it fully leaves
        threshold: 0
      }
    );

    observer.observe(firstDayGroupRef.current);

    return () => {
      observer.disconnect();
    };
  }, [activities]);

  const scrollToTop = useCallback(() => {
    if (hasTodayInFeed && firstDayGroupRef.current) {
      scrollPageIntoView(firstDayGroupRef.current, { block: 'start', behavior: 'smooth' });
    } else {
      scrollPageTo({ top: 0, behavior: 'smooth' });
    }
  }, [hasTodayInFeed]);

  const refreshProfile = useCallback(async ({ shareInFlight = false, throwOnError = false } = {}) => {
    const requestId = ++profileRequestRef.current;
    try {
      const profile = await backendService.fetchUserProfile({ shareInFlight });
      if (!isLatestStartupRequest(requestId, profileRequestRef.current)) return;
      setUserProfile(profile);
      userProfileRef.current = profile;
    } catch (e) {
      if (isLatestStartupRequest(requestId, profileRequestRef.current)) {
        console.error("Failed to fetch profile for nutrition stats", e);
      }
      if (throwOnError) throw e;
    } finally {
      if (isLatestStartupRequest(requestId, profileRequestRef.current)) {
        setProfileSettled(true);
      }
    }
  }, [backendService]);

  useEffect(() => {
    setProfileSettled(Boolean(userProfileRef.current));
    refreshProfile({ shareInFlight: shouldShareStartupProfileRequest(dojoChangeVersion) });
  }, [refreshProfile, dojoChangeVersion]);

  useEffect(() => {
    const handleProposalUpdated = event => {
      const memberId = event.detail?.nutritionMemberId;
      const profile = userProfileRef.current;
      const currentUserId = profile?._id || profile?.id || profile?.user_id;
      if (event.detail?.status !== 'approved' || !memberId || !currentUserId
          || String(memberId) !== String(currentUserId)) return;
      // Read the canonical allowance (including burn/ranges/macros), not the
      // proposal's raw calorie total. Keep feed pages and member selection intact.
      // The existing request fence rejects any older profile completion.
      refreshProfile({ shareInFlight: false });
    };
    window.addEventListener('regimen:strategy-proposals-updated', handleProposalUpdated);
    return () => window.removeEventListener('regimen:strategy-proposals-updated', handleProposalUpdated);
  }, [refreshProfile]);

  // Check if user has a workout plan
  useEffect(() => {
    const checkAffiliation = async () => {
      try {
        const affiliation = await backendService.fetchUserAffiliation();
        setHasWorkoutPlan(affiliation && affiliation.activity_plan_id);
      } catch (e) {
        // If error, assume they might have a plan to avoid showing banner incorrectly
        console.error("Failed to fetch user affiliation", e);
      }
    };
    checkAffiliation();
  }, [backendService]);

  const [, setAwaitingStatus] = useState(false); // legacy: backend no longer gates on status; kept harmless to avoid wider refactor

  useEffect(() => {
    // Check for recommendations
    let active = true;
    backendService.fetchRecommendations()
      .then(data => {
        if (!active) return;
        if (data?.awaiting_status || data?.status === 'awaiting_status') {
            setAwaitingStatus(true);
            const today = getDateKeyForUser(new Date());
            setLatestRecommendation(null);
            setRecommendationsByDate(prev => {
              const next = { ...prev };
              delete next[today];
              return next;
            });
            return;
        }
        setAwaitingStatus(false);
        if (data) {
            setLatestRecommendation(data);
            // Also store by date for day sections
            // Use target_date from recommendation, or default to today in user timezone.
            const recDate = data.target_date || getDateKeyForUser(new Date());
            setRecommendationsByDate(prev => ({
                ...prev,
                [recDate]: data
            }));
        }
      })
      .catch(error => {
        if (active) console.error('Failed to fetch recommendations', error);
      });

    // WebSocket Connection with auto-reconnect
    let ws = null;
    let heartbeatInterval = null;
    let reconnectTimeout = null;
    const MAX_RECONNECT_DELAY = 30000; // Max 30 seconds between reconnect attempts
    const BASE_RECONNECT_DELAY = 1000; // Start with 1 second
    const AUTH_ACK_COMPATIBILITY_DELAY = 5000;
    const authLifecycle = createWebSocketAuthLifecycle({
        baseDelay: BASE_RECONNECT_DELAY,
        maxDelay: MAX_RECONNECT_DELAY,
    });
    
    // Build WebSocket URL
    const getWsUrl = () => {
        if (process.env.REACT_APP_WS_URL) {
            return process.env.REACT_APP_WS_URL;
        } else if (API_BASE_URL) {
            const apiUrl = new URL(API_BASE_URL || window.location.origin);
            const protocol = apiUrl.protocol === 'https:' ? 'wss:' : 'ws:';
            // Keep the client path identical to the backend WebSocket.Server
            // path. The ws server is mounted at `/ws` (without a trailing
            // slash); Heroku otherwise closes the upgrade with code 1006.
            return `${protocol}//${apiUrl.host}/ws`;
        } else {
            const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
            return `${protocol}//${window.location.host}/ws`;
        }
    };
    
    // Guard against acting on an event that belongs to a different user.
    // Server-side fan-out is already per-user, so this is defence in depth
    // for any legacy broadcast payload that still carries a userId.
    const isForeignUserEvent = (msg) => {
        const localId = userProfileRef.current?._id || userProfileRef.current?.id;
        return Boolean(msg.userId && localId && String(msg.userId) !== String(localId));
    };

    // Any time the socket was down (backgrounded tab, sleeping phone, network
    // blip) we may have missed pushes, and nothing would ever repair the feed
    // short of a manual page refresh. Pull a fresh first page instead.
    let lastResyncAt = 0;
    let authRetryTimer = null;
    let authAckCompatibilityTimer = null;
    const clearAuthRetry = () => {
        if (authRetryTimer) {
            clearInterval(authRetryTimer);
            authRetryTimer = null;
        }
    };
    const resyncActivities = () => {
        if (!active || !secondaryHydrationRef.current) return;
        const now = Date.now();
        if (now - lastResyncAt < 5000) return; // avoid hammering on rapid tab flips
        lastResyncAt = now;
        if (fetchActivitiesRef.current) {
            // Pass loadThroughUnexpired=true so an ongoing scheduled workout
            // that was created on an earlier day (a "catch-up" workout the
            // user is doing before it expires) is re-fetched through the
            // oldest actionable activity, not silently dropped when it falls
            // past the first page. Pass reconcile=true so the merge happens
            // in place without clearing the list: the scroll position holds
            // and any uncommitted input edits survive the resume.
            fetchActivitiesRef.current(0, true, true);
        }
        fetchDojoFeed();
    };

    const connectWebSocket = () => {
        if (!active) return;
        
        clearAuthRetry();
        if (authAckCompatibilityTimer) {
            clearTimeout(authAckCompatibilityTimer);
            authAckCompatibilityTimer = null;
        }
        // Clean up any existing connection
        if (ws) {
            ws.onclose = null; // Prevent triggering reconnect from cleanup
            ws.close();
        }
        if (heartbeatInterval) {
            clearInterval(heartbeatInterval);
            heartbeatInterval = null;
        }
        
        try {
            const wsUrl = getWsUrl();
            ws = new WebSocket(wsUrl);
            
            // Store reference globally for other components
            window.appWebSocket = ws;
            
            ws.onopen = () => {
                console.log('Connected to WS for recommendations');
                
                // Authenticate with a verified session token. The user id is
                // sent only as a consistency check; the server derives identity
                // from the token and rejects spoofed ids.
                const socket = ws;
                const trySendAuth = async () => {
                    if (!socket || socket.readyState !== WebSocket.OPEN) return false;
                    const user = backendService.getUser ? backendService.getUser() : null;
                    const userId = user && (user._id || user.user_id || user.id);
                    let token = null;
                    try {
                        token = backendService.getToken ? await backendService.getToken({ skipCache: true }) : null;
                    } catch (tokenError) {
                        console.warn('WebSocket token unavailable:', tokenError?.message);
                    }
                    if (!userId || !token || !socket || socket.readyState !== WebSocket.OPEN) return false;
                    socket.send(JSON.stringify({ type: 'auth', user_id: userId, token }));
                    console.log('WebSocket authentication sent for user:', userId);
                    // Rolling deploy compatibility: older backends do not send
                    // the explicit acknowledgement. Rejected sockets close before
                    // this bounded grace period; a surviving socket is authenticated.
                    authAckCompatibilityTimer = setTimeout(() => {
                        if (!socket || socket.readyState !== WebSocket.OPEN || authLifecycle.isAuthenticated()) return;
                        const { shouldResync } = authLifecycle.authenticated();
                        if (shouldResync) resyncActivities();
                    }, AUTH_ACK_COMPATIBILITY_DELAY);
                    return true;
                };

                clearAuthRetry();
                trySendAuth().then((authenticated) => {
                    if (authenticated || !socket || socket.readyState !== WebSocket.OPEN) return;
                    // Profile/token not resolved yet — keep trying so we never
                    // end up silently connected but unauthenticated.
                    authRetryTimer = setInterval(async () => {
                        if (await trySendAuth() || !socket || socket.readyState !== WebSocket.OPEN) {
                            clearAuthRetry();
                        }
                    }, 1000);
                });

                // Start heartbeat. Skipped while backgrounded: the socket does not
                // need keeping alive when nothing is listening, and the resume
                // handler below reconnects if the server dropped us meanwhile.
                heartbeatInterval = setInterval(() => {
                    if (!isForeground()) return;
                    if (ws && ws.readyState === WebSocket.OPEN) {
                        ws.send(JSON.stringify({ type: 'heartbeat' }));
                    }
                }, 30000);
            };
            
            ws.onclose = (event) => {
                console.log('WebSocket closed:', event.code, event.reason);
                window.appWebSocket = null;
                if (authAckCompatibilityTimer) {
                    clearTimeout(authAckCompatibilityTimer);
                    authAckCompatibilityTimer = null;
                }
                
                if (heartbeatInterval) {
                    clearInterval(heartbeatInterval);
                    heartbeatInterval = null;
                }
                
                // Auto-reconnect with exponential backoff. Skipped while backgrounded
                // so we don't burn the radio retrying for a screen nobody is looking
                // at; the resume handler reconnects immediately instead.
                if (event.code === 1008) {
                    authLifecycle.authenticationRejected();
                }
                if (active && isForeground() && authLifecycle.canReconnect()) {
                    const delay = authLifecycle.closed();
                    console.log(`WebSocket reconnecting in ${delay}ms`);
                    reconnectTimeout = setTimeout(connectWebSocket, delay);
                }
            };
            
            ws.onerror = (error) => {
                console.error('WebSocket error:', error);
                // onclose will be called after onerror, which handles reconnection
            };

            ws.onmessage = (event) => {
                try {
                    const msg = JSON.parse(event.data);
                    // Content-free receipt time; ignores foreign/unknown types by flow allowlist.
                    if (!isForeignUserEvent(msg)) noteCoachWebSocketMessage(msg);
                    if (msg.type === 'authenticated') {
                        if (authAckCompatibilityTimer) {
                            clearTimeout(authAckCompatibilityTimer);
                            authAckCompatibilityTimer = null;
                        }
                        const { shouldResync } = authLifecycle.authenticated();
                        if (shouldResync) resyncActivities();
                        return;
                    }
                    if (['recommendation_generation_started', 'recommendation_ready', 'daily_insight_available', 'coach_timeline_updated', 'status_changed'].includes(msg.type)
                        && isForeignUserEvent(msg)) return;
                    if (['recommendation_generation_started', 'recommendation_ready', 'coach_timeline_updated'].includes(msg.type)) {
                        // Invalidates any canonical HTTP snapshot already in
                        // flight so it cannot overwrite this newer live signal.
                        coachPresenceSignalRef.current += 1;
                    }
                    if (msg.type === 'recommendation_generation_started') {
                        if (msg.kind === 'activity_update') {
                            setActivityUpdateInFlight(true);
                            window.dispatchEvent(new CustomEvent('coach-timeline-updated'));
                        } else {
                            setIsGeneratingRec(true);
                            isGeneratingRecRef.current = true;
                            setLatestRecommendation(null);
                        }
                    } else if (msg.type === 'daily_insight_available') {
                        readAvailableDailyInsight({
                            message: msg,
                            getCurrentStatusContextId: () => currentStatusContextIdRef.current,
                            fetchRecommendation: () => readCanonicalDailyRecommendation(backendService),
                            isForContext: isRecommendationForStatusContext,
                        }).then(recommendation => {
                            if (!recommendation) return;
                            noteCoachContextMatch('readiness_review', String(recommendation.status_context_id || ''));
                            showCoachReadyToast(coachReadyNotificationKey({ type: 'recommendation_ready', recommendation }), recommendation);
                            notifyAppliedMediaRequirementUpdate(recommendation);
                            setIsGeneratingRec(false);
                            isGeneratingRecRef.current = false;
                            setLatestRecommendation(recommendation);
                            const recDate = recommendation.target_date || getDateKeyForUser(new Date());
                            setRecommendationsByDate(prev => ({ ...prev, [recDate]: recommendation }));
                            window.dispatchEvent(new CustomEvent('coach-timeline-updated'));
                        }).catch(error => {
                            console.error('Failed to reread available Daily Insight:', error);
                        });
                    } else if (msg.type === 'recommendation_ready') {
                        if (isDailyFeedRecommendation(msg.recommendation)
                            && !isRecommendationForStatusContext(
                                msg.recommendation,
                                currentStatusContextIdRef.current
                            )) {
                            return;
                        }
                        if (isDailyFeedRecommendation(msg.recommendation)) {
                            showCoachReadyToast(coachReadyNotificationKey(msg), msg.recommendation);
                        }
                        notifyAppliedMediaRequirementUpdate(msg.recommendation);
                        setIsGeneratingRec(false);
                        isGeneratingRecRef.current = false;
                        setActivityUpdateInFlight(false);
                        // Clear any pending fallback poll
                        if (recommendationPollTimeoutRef.current) {
                            clearTimeout(recommendationPollTimeoutRef.current);
                            recommendationPollTimeoutRef.current = null;
                        }
                        // Event-triggered Coach updates belong in Coach chat and
                        // must not replace the normal daily insight in the feed.
                        if (isDailyFeedRecommendation(msg.recommendation)) {
                            window.dispatchEvent(new CustomEvent('coach-timeline-updated'));
                            setLatestRecommendation(msg.recommendation);
                            const recDate = msg.recommendation?.target_date || getDateKeyForUser(new Date());
                            setRecommendationsByDate(prev => ({
                                ...prev,
                                [recDate]: msg.recommendation
                            }));
                        }
                    } else if (msg.type === 'coach_timeline_updated') {
                        // Completion/expiration is visible immediately, before
                        // the debounce worker starts generation. Retry state also
                        // remains active. Generated/failed is terminal.
                        setActivityUpdateInFlight((current) => activityReviewPresenceFromTimelineMessage(current, msg));
                        if (msg.state === 'generated') {
                            resolveCoachReviewToast(msg.eventId, msg.insightId);
                            publishCoachReactionTyping(msg.eventId, false);
                        } else if (msg.state === 'failed') {
                            publishCoachReactionTyping(msg.eventId, false);
                            const failure = coachReviewFailureFromTimelineMessage(
                              msg,
                              userProfileRef.current?.ai_coach_settings?.name || null,
                            );
                            if (failure) setCoachReviewToasts(current => failCoachReviewToast(current, failure));
                        } else if (msg.state === 'reaction' && msg.replyWorthwhile === false) {
                            publishCoachReactionTyping(msg.eventId, false);
                            setCoachReviewToasts(current => removeCoachReviewToast(current, msg.eventId));
                        }
                        refreshCoachPresenceEvents();
                        window.dispatchEvent(new CustomEvent('coach-timeline-updated', { detail: { eventId: msg.eventId ? String(msg.eventId) : null } }));
                    } else if (msg.type === 'status_changed') {
                        // Route every same-user status change through the same lifecycle used
                        // by the local save, including the exact persisted context ID.
                        window.dispatchEvent(new CustomEvent('daily-status-saved', {
                            detail: {
                                status: msg.status,
                                statusContextId: msg.statusContextId,
                                statusCreatedAt: msg.statusCreatedAt,
                                externalDailyTaskId: msg.externalDailyTaskId,
                                externalDailyFailureCode: msg.externalDailyFailureCode,
                                externalDailyCapturePending: msg.externalDailyCapturePending === true,
                            },
                        }));
                        setRecommendationVersion(prev => prev + 1);
                    } else if (msg.type === 'travel_replacement_allowance_changed') {
                        if (isForeignUserEvent(msg)) return;
                        window.dispatchEvent(new Event('regimen:travelReplacements:changed'));
                    } else if (msg.type === 'activities_created') {
                        // New activities were scheduled by the backend - add them to the list
                        if (isForeignUserEvent(msg)) return;
                        console.log(`[WS] ${msg.count} new activities created, refreshing list`);
                        (msg.activities || []).filter(row => row.type === 'metric').forEach(row =>
                            handleActivityRefreshRef.current?.(row, { canonicalMutation: true }));
                        if (msg.activities && msg.activities.length > 0) {
                            setActivities(prevActivities => {
                                // Add new activities, avoiding duplicates
                                const existingIds = new Set(prevActivities.map(a => a._id));
                                const coordinator = workoutStartScope.current.state;
                                const newActivities = msg.activities
                                    .filter(a => a.type !== 'metric' && !existingIds.has(a._id) && !coordinator.isDeleted(a._id))
                                    .map(a => coordinator.has(a._id) ? coordinator.acknowledge(a) : a);
                                if (newActivities.length === 0) return prevActivities;
                                // Merge and sort by created_at descending
                                const merged = [...newActivities, ...prevActivities];
                                merged.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
                                return merged;
                            });
                        }
                    } else if (msg.type === 'activities_updated') {
                        // Backend rewrote whole activity documents (e.g. penalty recalc)
                        if (isForeignUserEvent(msg)) return;
                        (msg.activities || []).forEach(updated => {
                            if (updated && handleActivityRefreshRef.current) {
                                handleActivityRefreshRef.current(updated, { canonicalMutation: updated.type === 'metric' });
                            }
                        });
                    } else if (msg.type === 'activities_deleted') {
                        // Activities were removed on another device / by the backend
                        if (isForeignUserEvent(msg)) return;
                        const removedIds = (msg.activityIds || msg.activity_ids || []).map(String);
                        if (removedIds.length === 0) return;
                        console.log(`[WS] ${removedIds.length} activities deleted, pruning list`);
                        const removedSet = new Set(removedIds);
                        removedIds.forEach(id => workoutStartScope.current.state.forget(id));
                        setActivities(prevActivities =>
                            prevActivities.filter(a => !removedSet.has(String(a._id)))
                        );
                    } else if (msg.type === 'camera_media_updated') {
                        // Another owner device changed a camera parent: the typed
                        // camera feed rereads it (generic detail excludes it).
                        if (isForeignUserEvent(msg)) return;
                        window.dispatchEvent(new CustomEvent('camera-media-changed', { detail: { identity: backendService.authenticatedIdentity, ownerId: backendService.positionOwner } }));
                    } else if (msg.type === 'workout_exercise_review_published') {
                        // Content-free wake for a pending set review; Workout
                        // rereads the canonical fenced GET before rendering.
                        if (isForeignUserEvent(msg)) return;
                        window.dispatchEvent(new CustomEvent('workout-exercise-review-published', { detail: {
                            activityId: String(msg.activityId || ''),
                            workoutExerciseId: String(msg.workoutExerciseId || ''),
                            taskId: String(msg.taskId || ''),
                        } }));
                    } else if (msg.type === 'activity_updated') {
                        // Another device modified this activity — re-fetch and merge
                        const scope = workoutStartScope.current;
                        const readSequence = scope.state.snapshot();
                        backendService.fetchActivity(msg.activityId)
                            .then(updated => {
                                if (workoutStartScope.current !== scope || !scope.state.isActive()
                                  || backendService.authenticatedIdentity !== scope.identity
                                  || backendService.positionOwner !== scope.positionOwner) return;
                                const projected = scope.state.reconcile([updated], readSequence).find(row => String(row._id) === String(updated._id));
                                if (projected) handleActivityRefreshRef.current?.(projected);
                            })
                            .catch(err => console.error('[WS] activity_updated fetch failed', err));
                    } else if (msg.type === 'dojo_activity_update') {
                        // Live dojo pulse: a dojo member started or completed an activity
                        console.log(`[WS] Dojo activity ${msg.event}:`, msg.activity?.type);
                        setDojoFeedData(prev => {
                            const updatedActivities = [...prev.activities];
                            const idx = updatedActivities.findIndex(a => a._id === msg.activity?._id);
                            if (idx >= 0) {
                                if (!shouldApplyActivityRevision(updatedActivities[idx], msg.activity)) return prev;
                                // Merge: preserve existing summaries if WS payload lacks them
                                updatedActivities[idx] = {
                                    ...updatedActivities[idx],
                                    ...msg.activity,
                                    source: {
                                        ...updatedActivities[idx].source,
                                        ...msg.activity.source,
                                    },
                                    nutrition_summary: msg.activity.nutrition_summary || updatedActivities[idx].nutrition_summary,
                                    volume_summary: msg.activity.volume_summary || updatedActivities[idx].volume_summary,
                                };
                            } else if (msg.activity) {
                                // New activity
                                updatedActivities.unshift(msg.activity);
                            }
                            // Merge user if not already known
                            const updatedUsers = [...prev.users];
                            if (msg.user && !updatedUsers.some(u => u._id === msg.user._id)) {
                                updatedUsers.push(msg.user);
                            }
                            const next = { ...prev, users: updatedUsers, activities: updatedActivities };
                            dojoFeedDataRef.current = next;
                            return next;
                        });
                        // A completion-time correction can move one activity between
                        // two projected agenda days. Reconcile every member against
                        // the canonical dojo feed after applying the live update.
                        // Workout pulses omit the computed canonical score. Re-read
                        // the authorized feed rather than infer it from volume.
                        if (msg.event === 'updated' || msg.activity?.type === 'workout') fetchDojoFeed();
                        // Show toast notification
                        const localUserId = userProfileRef.current?._id || userProfileRef.current?.id;
                        const isOwnActivity = dojoToastIdentityId(msg.user) === dojoToastIdentityId(localUserId);
                        if (msg.user && msg.activity && !isOwnActivity) {
                            const typeLabels = { workout: 'a workout', meal: 'a meal', media: 'a progress photo', metric: 'body metrics', metrics: 'body metrics', survey: 'a check-in' };
                            const label = typeLabels[msg.activity.type] || msg.activity.type;
                            const name = msg.user.display_name || 'A dojo member';
                            // Peer updates may announce; self-completion is silent at
                            // the common toast ingress (including announcement echoes).
                            if (msg.event === 'completed') {
                                showDojoPill(`${name} completed ${label}`, msg.activity._id, msg.user, msg.activity.type, 'completed', {
                                  mediaFiles: msg.activity.type === 'media' ? msg.activity.data?.files : [],
                                  completionRevision: msg.activity.completed_at,
                                  completionTimeRevision: msg.activity.completion_time_revision,
                                  ownerId: msg.activity.user_id,
                                });
                            } else if (msg.event === 'started') {
                                showDojoPill(`${name} started ${label}`, msg.activity._id, msg.user, msg.activity.type, 'started', {
                                  mediaFiles: msg.activity.type === 'media' ? msg.activity.data?.files : [],
                                  completionRevision: msg.activity.completed_at,
                                  completionTimeRevision: msg.activity.completion_time_revision,
                                  ownerId: msg.activity.user_id,
                                });
                            }
                        }
                    } else if (msg.type === 'social_activity_update') {
                        // Privacy-filtered event for an authorized friend or dojo member.
                        mergeSocialPresenceEvent(msg);
                        setPresenceRevision(prev => prev + 1);
                        if (dojoFeedDataRef.current.dojoId && msg.activity?.type === 'media'
                            && ['complete', 'completed'].includes(msg.activity.status)) {
                          // Event hints are not a replacement for current membership and
                          // audience checks (e.g. chief-only media). Read canonically.
                          fetchDojoFeed();
                        }
                        } else if (msg.type === 'dojo_activity_announcement') {
                          const announcement = typeof msg.announcement === 'string' ? msg.announcement.trim() : '';
                          if (!announcement || !msg.activity_id) {
                            return;
                          }
                          const eventDojoId = String(msg.dojo_id || '');
                          if (!eventDojoId || eventDojoId !== String(dojoFeedDataRef.current.dojoId || '')) {
                            return;
                          }

                          const sourceActivity = dojoFeedDataRef.current.activities.find(activity => String(activity._id) === String(msg.activity_id));
                          const announcementMediaFiles = msg.activity_type === 'media'
                            ? (sourceActivity?.data?.files?.length ? sourceActivity.data.files : (msg.media_files || []))
                            : [];

                          // Always retain the durable public comment, including self.
                          // Only the transient toast below is identity-filtered.
                          const coachComment = {
                            ...(msg.coach_comment || {
                              _id: `live:${msg.activity_id}`,
                              activity_id: msg.activity_id,
                              subject_user_id: msg.user_id,
                              subject_name: msg.user_name || 'Member',
                              subject_avatar_url: msg.user_avatar_url || null,
                              text: announcement,
                              coach_emoji: msg.coach_emoji || '🥋',
                              created_at: new Date().toISOString(),
                            }),
                            media_files: announcementMediaFiles,
                            _received_at: new Date().toISOString(),
                            _dojo_id: eventDojoId,
                          };
                          setDojoFeedData(prev => {
                            const updatedActivities = prev.activities.map(a =>
                              String(a._id) === String(msg.activity_id)
                                ? {
                                    ...a,
                                    dojo_announcement: announcement,
                                    dojo_announcement_emoji: msg.coach_emoji || '🥋',
                                    data: announcementMediaFiles.length
                                      ? { ...(a.data || {}), files: a.data?.files?.length ? a.data.files : announcementMediaFiles }
                                      : a.data,
                                  }
                                : a
                            );
                            const coachComments = selectRosterCoachComments([
                              coachComment,
                              ...(prev.coachComments || []),
                            ]);
                            const next = { ...prev, activities: updatedActivities, coachComments };
                            dojoFeedDataRef.current = next;
                            return next;
                          });

                          const copy = dojoCompletionCopy(msg, {
                            inDojo: dojoFeedDataRef.current.inDojo,
                            dojoId: dojoFeedDataRef.current.dojoId,
                            currentUserId: userProfileRef.current?._id || userProfileRef.current?.id,
                          });
                          showDojoPill(copy.message, msg.activity_id, {
                            _id: msg.user_id, display_name: msg.user_name || 'Member', avatar_url: msg.user_avatar_url || null,
                          }, msg.activity_type, 'completed', {
                            coachHighlight: copy.coachHighlight, timerDuration: 8,
                            mediaFiles: announcementMediaFiles,
                            completionRevision: msg.completion_revision,
                            completionTimeRevision: msg.completion_time_revision,
                            priority: 1,
                            ownerId: sourceActivity?.user_id || msg.user_id,
                          });
                    }
                } catch (e) {
                    console.error('WS parse error', e);
                }
            };
        } catch (e) {
            console.error('WS connection failed', e);
            // Try to reconnect after a delay
            if (active && isForeground()) {
                const delay = authLifecycle.closed();
                reconnectTimeout = setTimeout(connectWebSocket, delay);
            }
        }
    };
    
    // Reconnect whenever the app comes back to the front, since we stop retrying
    // while it is away.
    const handleForegroundChange = (isActive) => {
        if (!isActive || !active) return;
        refreshCoachPresenceEvents();
        if ((!ws || ws.readyState !== WebSocket.OPEN) && authLifecycle.canReconnect()) {
            console.log('App returned to foreground, reconnecting WebSocket...');
            if (reconnectTimeout) {
                clearTimeout(reconnectTimeout);
                reconnectTimeout = null;
            }
            connectWebSocket();
        } else if (authLifecycle.isAuthenticated()) {
            // Socket survived, but pushes may still have been dropped while the
            // app was hidden — reconcile against the server.
            resyncActivities();
        }
    };
    
    const unsubscribeForeground = subscribeForeground(handleForegroundChange);
    
    // Initial connection
    connectWebSocket();

    return () => { 
        active = false; 
        unsubscribeForeground();
        clearAuthRetry();
        if (authAckCompatibilityTimer) clearTimeout(authAckCompatibilityTimer);
        if (reconnectTimeout) {
            clearTimeout(reconnectTimeout);
        }
        if (ws) {
            ws.onclose = null; // Prevent reconnect on intentional close
            window.appWebSocket = null;
            ws.close();
        }
        // Clear heartbeat interval
        if (heartbeatInterval) {
            clearInterval(heartbeatInterval);
        }
        // Clear any pending recommendation poll
        if (recommendationPollTimeoutRef.current) {
            clearTimeout(recommendationPollTimeoutRef.current);
            recommendationPollTimeoutRef.current = null;
        }
    };
  }, [backendService, fetchDojoFeed, getDateKeyForUser, mergeSocialPresenceEvent, refreshCoachPresenceEvents, resolveCoachReviewToast, showCoachReadyToast, showDojoPill]);

  const regenerateDailyInsight = async () => {
      if (isGeneratingRec || !backendService?.forceRegenerateRecommendation) return;
      setIsGeneratingRec(true);
      isGeneratingRecRef.current = true;
      try {
          const recommendation = await backendService.forceRegenerateRecommendation();
          if (!recommendation) throw new Error('The insight could not be regenerated.');
          showCoachReadyToast(coachReadyNotificationKey({ type: 'recommendation_ready', recommendation }), recommendation);
          const recDate = recommendation.target_date || getDateKeyForUser(new Date());
          setLatestRecommendation(recommendation);
          setRecommendationsByDate(prev => ({ ...prev, [recDate]: recommendation }));
      } catch (error) {
          console.error('Daily insight regeneration failed:', error);
          modalService.alert?.('Unable to regenerate your insight right now. Please try again.');
      } finally {
          setIsGeneratingRec(false);
          isGeneratingRecRef.current = false;
      }
  };

  const openRecommendations = () => {
      if (isGeneratingRec) return; 
      modalService.open(RecommendationsModal, { 
          backendService, 
          modalService,
          existingData: latestRecommendation, 
          close: () => {
              backendService.markRecommendationSeen(latestRecommendation._id);
              setLatestRecommendation(null); 
              modalService.close(); 
          }
       });
  };

  const reconcileApprovedWorkoutActivities = async (_proposal, result) => {
      await refreshApprovedWorkoutActivities({
          result,
          fetchActivity: id => backendService.fetchActivity(id),
          setActivities
      });
  };

  // Open the full coach chat (same hub reachable from the menu). The feed's
  // inline reply hands its first message off here via `seed*` so there is
  // exactly one coach conversation — the thread inside the hub — rather than
  // a separate ephemeral mini-chat on the feed.
  const openCoachChat = (options = {}) => {
      modalService.open(CoachHubModal, {
          backendService,
          modalService,
          seedMessage: options.seedMessage,
          seedAttachments: options.seedAttachments,
          exerciseScope: options.exerciseScope,
          focusInsightId: options.focusInsightId,
          focusTimelineEntryId: options.focusTimelineEntryId,
          showDailyCoachFailure: options.showDailyCoachFailure,
          coachIsThinking,
          onProposalApprove: reconcileApprovedWorkoutActivities,
      });
  };

  const appendActivities = (newActivities, readSequence) => {
    newActivities = workoutStarts.reconcile(newActivities, readSequence);
    setActivities(prevActivities => {
      const filteredNewActivities = newActivities.filter(
        newActivity => !prevActivities.some(existingActivity => existingActivity._id === newActivity._id)
      );
      const updatedActivities = [...prevActivities, ...filteredNewActivities].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

      return updatedActivities;
    });
  };

  // Reconcile a partial fresh snapshot without replacing the already-loaded
  // tail. Reconnect snapshots are intentionally bounded, so treating them as
  // the complete feed makes older-created rows disappear even when they are
  // displayed on a recent completion day.
  const reconcileActivities = useCallback((fresh, preserveBeforeCreatedAt = null, readSequence) => {
    setActivities(prev => reconcileActivitySnapshot(prev, workoutStarts.reconcile(fresh, readSequence), preserveBeforeCreatedAt));
  }, [workoutStarts]);

  const fetchActivities = async (offset = 0, loadThroughUnexpired = false, reconcile = false, transportOptions = {}) => {
    const requestVersion = activityFilterVersion;
    // Deduplicate requests for one filter version, but never let an older
    // filter request block the new filter's required first page.
    if (isLoadingRef.current?.filterVersion === requestVersion) return null;
    const requestId = ++activityRequestIdRef.current;
    const readSequence = workoutStarts.snapshot();
    isLoadingRef.current = { requestId, filterVersion: requestVersion };
    let succeeded = false;
    if (offset === 0 && !reconcile) setActivityLoadError(false);

    try {
      const { startDate, endDate, types, statuses } = filters;

      const selectedTypes = types ? Object.keys(types).filter((t) => !!types[t]) : [];
      const selectedStatuses = statuses ? Object.keys(statuses).filter((s) => !!statuses[s]) : [];

      // Treat "none selected" and "all selected" as "all" (no backend filter).
      const allTypeCount = Object.keys(types || {}).length;
      const typesParam = selectedTypes.length === 0 || selectedTypes.length === allTypeCount ? null : selectedTypes;
      const allStatusCount = Object.keys(statuses || {}).length;
      const statusesParam = selectedStatuses.length === 0 || selectedStatuses.length === allStatusCount ? null : selectedStatuses;

      let currentOffset = offset;
      let currentCursor = transportOptions.cursor || null;
      let isFirstPage = offset === 0;
      const reconcileThroughOffset = reconcile ? nextOffsetRef.current : 0;
      const reconcileThroughCreatedAt = reconcile ? oldestConsumedCreatedAtRef.current : null;
      const reconcileThroughCursor = reconcile ? nextCursorRef.current : null;
      let oldestLoadedCreatedAt = null;
      // In reconcile mode we gather every fetched page and merge the whole
      // snapshot at once, so the list is never blanked mid-refresh.
      let reconcileBuffer = [];
      let reconcileDayStatuses = {};
      let reconcileHasAnyActivities = null;
      let settledNextOffset = nextOffsetRef.current;
      let settledNextCursor = nextCursorRef.current;
      let settledHasMore = hasMoreRef.current;
      const requestTransportOptions = offset === 0 && !reconcile
        ? { timeout: INITIAL_ACTIVITY_REQUEST_TIMEOUT_MS, ...transportOptions }
        : transportOptions;

      do {
        // The first request remains offset-compatible; every following request
        // uses the stable source cursor returned by the backend.
        const data = await backendService.fetchActivities(
          null,
          activitiesPerPage,
          null,
          startDate,
          endDate,
          typesParam,
          statusesParam,
          currentOffset,
          { ...requestTransportOptions, cursor: currentCursor },
        );
        // A filter transition invalidates every response from the prior
        // closure before it can mutate rows, statuses, or pagination refs.
        if (requestVersion !== activityFilterVersionRef.current) return null;

        if (reconcile) {
          reconcileBuffer = reconcileBuffer.concat(data.activities);
          reconcileDayStatuses = { ...reconcileDayStatuses, ...(data.dayStatuses || {}) };
          if (typeof data.hasAnyActivities === 'boolean') {
            reconcileHasAnyActivities = data.hasAnyActivities;
          }
        }

        if (isFirstPage) {
          if (!reconcile) {
            setActivities(workoutStarts.reconcile(data.activities, readSequence));
            setDayStatuses(data.dayStatuses || {});
            if (typeof data.hasAnyActivities === 'boolean') {
              setHasAnyActivities(data.hasAnyActivities);
            }
          }
          // Today's activities are in the first newest-first page. Mark the
          // critical path ready now; older catch-up pages continue below.
          if (offset === 0 && !reconcile) {
            setInitialLoadComplete(true);
            setPrimaryActivitiesSettled(true);
          }
          isFirstPage = false;
        } else if (!reconcile) {
          appendActivities(data.activities, readSequence);
          // Merge new day statuses with existing ones
          if (data.dayStatuses) {
            setDayStatuses(prev => ({ ...prev, ...data.dayStatuses }));
          }
        }

        // Track canonical source coverage supplied by the backend. Completion-
        // day extras can have a much older created_at despite not advancing the
        // source cursor, so deriving coverage from response rows is unsafe. The
        // response fallback only covers a brief mixed-version deployment.
        const pageSourceBoundary = data.oldestConsumedCreatedAt || data.activities.reduce((oldest, activity) => {
          if (!activity.created_at) return oldest;
          return !oldest || new Date(activity.created_at) < new Date(oldest) ? activity.created_at : oldest;
        }, null);
        if (pageSourceBoundary
          && (!oldestLoadedCreatedAt || new Date(pageSourceBoundary) < new Date(oldestLoadedCreatedAt))) {
          oldestLoadedCreatedAt = pageSourceBoundary;
        }

        // Initial loads must include every page through the oldest actionable
        // activity so catch-up work is visible without requiring a scroll.
        const oldestUnexpiredCreatedAt = data.oldestUnexpiredCreatedAt;
        const reachedOldestUnexpired = !oldestUnexpiredCreatedAt
          || (oldestLoadedCreatedAt && new Date(oldestLoadedCreatedAt) <= new Date(oldestUnexpiredCreatedAt));
        const nextOffset = data.nextOffset;
        const nextCursor = data.nextCursor || null;
        const cursorAdvanced = nextCursor && (
          !currentCursor
          || nextCursor.createdAt !== currentCursor.createdAt
          || nextCursor.id !== currentCursor.id
        );
        const canFetchNextPage = data.hasMore && (cursorAdvanced || nextOffset > currentOffset);

        settledNextOffset = nextOffset;
        settledNextCursor = nextCursor;
        settledHasMore = data.hasMore;
        if (!reconcile) {
          nextOffsetRef.current = nextOffset;
          nextCursorRef.current = nextCursor;
          oldestConsumedCreatedAtRef.current = oldestLoadedCreatedAt;
          setOldestPersonalSourceDate(oldestLoadedCreatedAt);
          hasMoreRef.current = data.hasMore;
          setHasMore(data.hasMore);
        }

        if (!shouldContinueActivityFetch({
          loadThroughUnexpired,
          reachedOldestUnexpired,
          reconcile,
          nextOffset,
          reconcileThroughOffset,
          oldestLoadedCreatedAt,
          reconcileThroughCreatedAt,
          nextCursor,
          reconcileThroughCursor,
          canFetchNextPage,
        })) break;
        currentOffset = nextOffset;
        currentCursor = nextCursor;
      } while (true);

      if (reconcile) {
        // Rows older than the refreshed source boundary were never queried;
        // preserve that tail while treating the refreshed range as canonical.
        reconcileActivities(reconcileBuffer, oldestLoadedCreatedAt, readSequence);
        const oldestRefreshedDayKey = oldestLoadedCreatedAt
          ? getDateKeyForUser(oldestLoadedCreatedAt)
          : null;
        setDayStatuses(previousDayStatuses => reconcileDayStatusSnapshot(
          previousDayStatuses,
          reconcileDayStatuses,
          oldestRefreshedDayKey,
        ));
        if (reconcileHasAnyActivities !== null) {
          setHasAnyActivities(reconcileHasAnyActivities);
        }
        nextOffsetRef.current = settledNextOffset;
        nextCursorRef.current = settledNextCursor;
        oldestConsumedCreatedAtRef.current = oldestLoadedCreatedAt;
        setOldestPersonalSourceDate(oldestLoadedCreatedAt);
        hasMoreRef.current = settledHasMore;
        setHasMore(settledHasMore);
      }

      if (offset === 0) {
        setInitialLoadComplete(true);
      }
      succeeded = true;
    } catch (error) {
      if (requestVersion === activityFilterVersionRef.current) {
        console.error('Error fetching activities:', error);
        if (offset === 0 && !reconcile) setActivityLoadError(true);
      }
    } finally {
      // A stale request must not clear or settle the current filter's request.
      if (isLoadingRef.current?.requestId === requestId) {
        // A failed first request must not leave the page trapped behind the
        // loading shell forever. Keep secondary startup traffic paused so an
        // explicit retry still owns the primary request lane.
        if (offset === 0 && !reconcile) {
          setInitialLoadComplete(true);
        }
        isLoadingRef.current = false;
        setActivityPaginationRevision(revision => revision + 1);
      }
    }
    return succeeded;
  };

  const fetchExercises = async () => {
    try {
      const data = await backendService.fetchExercises();
      setExercises(data);
    } catch (error) {
      console.error('Error fetching exercises:', error);
    }
  };

  useEffect(() => {
    if (!startupRequestPlan.secondaryHydration) return;
    fetchExercises();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startupRequestPlan.secondaryHydration]);

  // Trigger initial load and reload when filters change
  useEffect(() => {
    nextOffsetRef.current = 0;
    nextCursorRef.current = null;
    oldestConsumedCreatedAtRef.current = null;
    setOldestPersonalSourceDate(null);
    hasMoreRef.current = true;
    setHasMore(true);
    fetchActivities(0, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  // Invalidate the old closure synchronously; waiting for the effect would
  // leave a microtask-sized window where its response could still commit.
  const handleFilterChange = useCallback((newFilters) => {
    activityFilterVersionRef.current += 1;
    // React batches these with the new filter render. Do not allow that render
    // to reuse the prior filter's page boundary and claim missing self rows.
    setOldestPersonalSourceDate(null);
    hasMoreRef.current = true;
    setHasMore(true);
    setFilters(newFilters);
  }, []);

  // Update dojo feed locally so own activity changes reflect in the pulse immediately
  const updateDojoFeedActivity = useCallback((activity, event) => {
    if (!dojoFeedData.dojoName) return; // not in a dojo
    const userId = userProfile?._id || userProfile?.id;
    if (!userId) return;
    setDojoFeedData(prev => {
      const updatedActivities = [...prev.activities];
      const idx = updatedActivities.findIndex(a => a._id === activity._id);
      if (idx >= 0) {
        updatedActivities[idx] = { ...updatedActivities[idx], ...activity };
      } else {
        updatedActivities.unshift({ ...activity, user_id: userId });
      }
      // Ensure own user is in the users list
      const updatedUsers = [...prev.users];
      if (!updatedUsers.some(u => u._id === userId)) {
        updatedUsers.push({
          _id: userId,
          display_name: userProfile?.display_name || 'You',
          avatar_url: userProfile?.avatar_url || null,
          nutrition_targets: userProfile?.nutrition_targets || null
        });
      }
      const next = { ...prev, users: updatedUsers, activities: updatedActivities };
      dojoFeedDataRef.current = next;
      return next;
    });
  }, [dojoFeedData.dojoName, userProfile]);

  const pendingActivityFocusRef = useRef(null);

  const onNewActivity = (newActivity) => {
    pendingActivityFocusRef.current = newActivity?._id
      ? { activityId: String(newActivity._id), dateKey: null }
      : null;
    setActivities(prevActivities => [newActivity, ...prevActivities]);
    updateDojoFeedActivity(newActivity, 'started');
  };

  // A new or relocated activity is rendered in its natural type/date group. We
  // wait for the row to mount and become layout-stable before scrolling so its
  // final location is used.
  useEffect(() => {
    const focusRequest = pendingActivityFocusRef.current;
    if (!focusRequest?.activityId) return undefined;

    const HEADER_OFFSET = 160;
    const MAX_ATTEMPTS = 48; // approx 0.8s at 60fps
    const REQUIRED_STABLE_FRAMES = 2;
    let frameId;
    let attempts = 0;
    let lastDocumentTop = null;
    let stableFrames = 0;

    const focusPendingActivity = () => {
      const element = findFocusedActivityElement(focusRequest);
      if (!element) {
        if (attempts < MAX_ATTEMPTS) {
          attempts += 1;
          frameId = requestAnimationFrame(focusPendingActivity);
          return;
        }

        // Give up waiting once the element never materializes. Clearing the
        // pending marker prevents retries across unrelated renders.
        pendingActivityFocusRef.current = null;
        return;
      }

      // Track the row's document position, not its viewport-relative top.
      // Browser/history scroll restoration can move the viewport while the
      // modal closes even though the row itself has already settled.
      const documentTop = element.getBoundingClientRect().top + window.pageYOffset;
      if (lastDocumentTop === null || Math.abs(documentTop - lastDocumentTop) > 1) {
        lastDocumentTop = documentTop;
        stableFrames = 0;
      } else {
        stableFrames += 1;
      }

      if (stableFrames < REQUIRED_STABLE_FRAMES && attempts < MAX_ATTEMPTS) {
        attempts += 1;
        frameId = requestAnimationFrame(focusPendingActivity);
        return;
      }

      pendingActivityFocusRef.current = null;
      scrollPageTo({ top: Math.max(0, documentTop - HEADER_OFFSET), behavior: 'smooth' });
    };

    frameId = requestAnimationFrame(focusPendingActivity);
    return () => cancelAnimationFrame(frameId);
  }, [activities]);

  const handleActivityCompletion = (activityId) => {
    // ID-only callers (including AI-supervised editors) share the same fence.
    const completedWorkout = workoutStarts.complete(activityId);
    setActivities(prevActivities =>
      prevActivities.map(activity =>
        activity._id === activityId ? { ...(completedWorkout || activity), status: 'complete', social: activity.social || { kudos_count: 0, comment_count: 0, has_kudos: false } } : activity
      )
    );
    // Update dojo pulse with completed status
    updateDojoFeedActivity({ _id: activityId, status: 'complete', completed_at: new Date().toISOString() }, 'completed');
    // No self-completion toast: top-bar shimmer is the completion announcement.
    // The shared toast ingress also suppresses the later realtime praise echo;
    // canonical activity updates and durable Dojo notes remain untouched.
    // Always refresh profile after completion so in-session nutrition targets stay current.
    const refreshes = Promise.allSettled([
      refreshProfile({ throwOnError: true }),
      Promise.resolve().then(() => onActivityComplete()),
    ]).then(results => {
      const failure = results.find(result => result.status === 'rejected');
      if (failure) throw failure.reason;
    });
    // AI-supervised completion callers may intentionally not await this callback.
    // Observe their failures without changing the rejected promise awaited by Meal.
    refreshes.catch(error => console.error('Failed to refresh after activity completion:', error));
    return refreshes;
  };

  const handleActivityCancellation = (activityId) => {
    workoutStarts.forget(activityId);
    setCancelledShortcutWorkoutId(String(activityId));
    setActivities(prevActivities =>
      prevActivities.filter(activity => !(activity._id === activityId))
    );
  };

  const handleActivityRefreshRef = useRef(null);
  const handleActivityRefresh = (updatedActivity, options = {}) => {
    if (updatedActivity === null) {
      fetchActivities(true);
      return;
    }
    if (workoutStarts.isDeleted(updatedActivity._id)) return;
    if (updatedActivity.type === 'metric') {
      const owner = userProfileRef.current?._id || userProfileRef.current?.id;
      if (!owner || String(updatedActivity.user_id) !== String(owner)) return;
      workoutStarts.acknowledgeMutation(updatedActivity);
    }
    if (options.workoutStart || workoutStarts.has(updatedActivity._id)) {
      updatedActivity = workoutStarts.acknowledge(updatedActivity);
    }
    if (isOngoingWorkoutActivity(updatedActivity)) {
      setCancelledShortcutWorkoutId(previous => previous === String(updatedActivity._id) ? null : previous);
    }
    if (options.focusActivityId) {
      pendingActivityFocusRef.current = {
        activityId: options.focusActivityId,
        dateKey: getDateKeyForUser(options.focusCompletedAt),
      };
    }
    setActivities(prevActivities => {
      const rows = (options.workoutStart || options.canonicalMutation) && !prevActivities.some(row => row._id === updatedActivity._id)
        ? [...prevActivities, updatedActivity] : prevActivities;
      return rows.map(activity => {
        if (activity._id !== updatedActivity._id) return activity;
        const merged = { ...updatedActivity };
        if (!merged.social && activity.social) {
          merged.social = activity.social;
        }
        // Preserve previously-computed summaries when the incoming
        // payload omits them. Some endpoints (e.g. status mutations,
        // legacy WS fallbacks) return the raw mongo document without
        // attaching the on-the-fly nutrition_summary / volume_summary
        // fields. Without this guard the collapsed accordion macro
        // pill silently disappears after operations like reopen.
        if (!merged.nutrition_summary && activity.nutrition_summary) {
          merged.nutrition_summary = activity.nutrition_summary;
        }
        if (!merged.volume_summary && activity.volume_summary) {
          merged.volume_summary = activity.volume_summary;
        }
        return merged;
      });
    });
    updateDojoFeedActivity(updatedActivity, updatedActivity.status === 'complete' ? 'completed' : 'started');
    if (options.refreshDojoAgenda) fetchDojoFeed();

    // Workout calorie changes affect the nutrition budget — refresh targets
    if (updatedActivity.type === 'workout') {
      refreshProfile();
    }
  };
  handleActivityRefreshRef.current = handleActivityRefresh;

  // Lets the WebSocket effect (declared earlier) pull a fresh snapshot after a
  // reconnect or when the app returns to the foreground.
  const fetchActivitiesRef = useRef(null);
  fetchActivitiesRef.current = fetchActivities;

  const fetchMoreActivities = () => {
    // Use stable source cursors so concurrent inserts/deletes before the loaded
    // boundary cannot duplicate or skip rows. Offset remains a compatibility aid.
    return fetchActivities(nextOffsetRef.current, false, false, { cursor: nextCursorRef.current });
  };

  useEffect(() => {
    if (!initialLoadComplete || scrollRestoreStartedRef.current) return undefined;
    scrollRestoreStartedRef.current = true;
    const anchor = readActivityScrollAnchor();
    if (!anchor) return undefined;

    let cancelled = false;
    let restoreRequestController = null;
    const waitForLayout = () => new Promise(resolve => {
      requestAnimationFrame(() => requestAnimationFrame(resolve));
    });
    const waitForCurrentLoad = async () => {
      const state = await waitForActivityLoadIdle({
        isLoading: () => isLoadingRef.current,
        isCancelled: () => cancelled,
        expiresAt: anchor.expiresAt,
        delay: () => new Promise(resolve => window.setTimeout(resolve, 50)),
      });
      return state === 'idle';
    };
    const loadNextPage = async () => {
      const beforeOffset = nextOffsetRef.current;
      const beforeCount = activityCountRef.current;
      let attempts = 0;
      while (attempts < 3 && !cancelled && Date.now() < anchor.expiresAt) {
        if (!(await waitForCurrentLoad())) return false;
        if (nextOffsetRef.current > beforeOffset
          || activityCountRef.current > beforeCount
          || !hasMoreRef.current) {
          return true;
        }

        const pageOffset = nextOffsetRef.current;
        const controller = new AbortController();
        restoreRequestController = controller;
        const remainingMs = Math.max(1, anchor.expiresAt - Date.now());
        const succeeded = await fetchActivities(pageOffset, false, false, {
          timeout: Math.min(15000, remainingMs),
          signal: controller.signal,
          cursor: nextCursorRef.current,
        });
        if (restoreRequestController === controller) restoreRequestController = null;
        if (succeeded === null) continue;
        if (succeeded) {
          await waitForLayout();
          return nextOffsetRef.current > pageOffset
            || activityCountRef.current > beforeCount
            || !hasMoreRef.current;
        }
        attempts += 1;
        if (Date.now() >= anchor.expiresAt) return false;
        await new Promise(resolve => window.setTimeout(resolve, Math.min(250, anchor.expiresAt - Date.now())));
      }
      return false;
    };

    restoreActivityScrollAnchor({
      anchor,
      findActivity: activityId => findActivityScrollElement(activityId),
      loadNextPage,
      hasMore: () => hasMoreRef.current,
      waitForLayout,
      scrollBy: delta => scrollPageBy({ top: delta, left: 0, behavior: 'auto' }),
      clear: () => clearActivityScrollAnchor(),
      isCancelled: () => cancelled,
      isExpired: () => Date.now() >= anchor.expiresAt,
    });

    return () => {
      cancelled = true;
      restoreRequestController?.abort();
      restoreRequestController = null;
    };
    // This is a one-shot handoff from the update reload. The mutable refs keep
    // pagination state current without restarting the restoration worker.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialLoadComplete]);

  const retryInitialActivities = () => {
    if (!canStartInitialActivityRetry(isLoadingRef.current)) return;
    setActivityLoadError(false);
    setInitialLoadComplete(false);
    fetchActivities(0, true);
  };

  // Fetch recommendations for all dates in the activities list
  useEffect(() => {
    if (!activities.length) return;
    
    // Extract unique dates (YYYY-MM-DD format) from activities using local date
    const uniqueDates = new Set();
    activities.forEach(activity => {
      const dateStr = getDateKeyForUser(activity.created_at);
      uniqueDates.add(dateStr);
    });
    
    // Also add today if not present (user timezone).
    const todayStr = getDateKeyForUser(new Date());
    uniqueDates.add(todayStr);
    
    // Filter out dates we've already fetched
    const datesToFetch = [...uniqueDates].filter(d => !fetchedRecDatesRef.current.has(d));
    
    if (datesToFetch.length === 0) return;
    
    // Mark these dates as being fetched
    datesToFetch.forEach(d => fetchedRecDatesRef.current.add(d));
    
    // Fetch recommendations for all dates in batch
    backendService.fetchRecommendationsByDates(datesToFetch).then(data => {
      if (data && Object.keys(data).length > 0) {
        setRecommendationsByDate(prev => ({ ...prev, ...data }));
      }
    }).catch(err => {
      console.error('Error fetching recommendations by dates:', err);
      // On error, remove from fetched set so they can be retried
      datesToFetch.forEach(d => fetchedRecDatesRef.current.delete(d));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activities, backendService, getDateKeyForUser]);

  const openNewActivityModal = (options = {}) => {
    modalService.open(NewActivityModal, {
      onAdd: onNewActivity,
      backendService: backendService,
      ...options,
    });
  };

  const { action: todayAction, actions: todayActions, snapshot: todaySnapshot, retry: retryToday, now: todayNow, refreshing: todayRefreshing } = useTodayAction({
    backendService, activities, currentUserId: userProfile?._id || userProfile?.id || userProfile?.user_id,
    reconcileWorkoutStarts: workoutStarts.reconcile,
    captureReconciliation: workoutStarts.snapshot,
    getDateKey: getDateKeyForUser, hasPlan: Boolean(hasWorkoutPlan), locked: Boolean(activityInteractionLock),
    enabled: startupRequestPlan.secondaryHydration,
  });
  // A terminal primary failure leaves canonical/secondary reads disabled. Show
  // the existing retry state without treating the incomplete feed as canonical.
  const primaryTodayLoadFailed = !startupRequestPlan.secondaryHydration && initialLoadComplete && activityLoadError;
  const headerTodayActions = primaryTodayLoadFailed
    ? [{ kind: 'error', title: 'Today could not be loaded', label: 'Retry' }]
    : todayActions;
  const [todayReveal, setTodayReveal] = useState(null);
  const [showTodayOverride, setShowTodayOverride] = useState(false);
  const todayNavigationRef = useRef(null);
  const todayKeyForAction = getDateKeyForUser(todayNow);
  const previousTodayKeyRef = useRef(todayKeyForAction);
  useEffect(() => {
    if (activityInteractionLock || previousTodayKeyRef.current === todayKeyForAction) return;
    previousTodayKeyRef.current = todayKeyForAction;
    setTodayReveal(null);
    setShowTodayOverride(false);
  }, [todayKeyForAction, activityInteractionLock]);

  const showTodayActivities = () => {
    if (activityInteractionLock || todayRefreshing || ['loading','error','partial'].includes(todayAction.kind)) return;
    setActivities(previous => {
      const known = new Set(previous.map(todayActivityId));
      return [...previous, ...todaySnapshot.activities.filter(a => !known.has(todayActivityId(a)))];
    });
    setShowTodayOverride(true);
    todayNavigationRef.current = {day: true};
  };
  const handleTodayAction = (selectedAction = todayAction) => {
    if (activityInteractionLock) return;
    if (todayRefreshing) return;
    if (selectedAction.kind === 'error' || selectedAction.kind === 'partial') {
      if (primaryTodayLoadFailed) retryInitialActivities();
      else retryToday();
      return;
    }
    if (selectedAction.activity) {
      const id = todayActivityId(selectedAction.activity);
      // This is navigation only: starting/resuming stays inside the existing editor.
      if (!todayActions.some(action => action.activity && todayActivityId(action.activity) === id)) {
        retryToday();
        return;
      }
      // Adopt the authorized occurrence into the existing mutation/reconciliation
      // store before revealing it. All editors now update/delete the same row,
      // even if the next network refresh fails. Pagination cursors stay untouched.
      setActivities(previous => previous.some(a => todayActivityId(a) === id) ? previous : [...previous, selectedAction.activity]);
      const request = {
        id,
        firstIncomplete: isOngoingWorkoutActivity(selectedAction.activity),
        onHandled: () => setTodayReveal(current => current === request ? { id } : current),
      };
      setTodayReveal(request);
      // The hydrated workout owns exercise selection and scrolling, including
      // the all-complete overview fallback. Do not race it with a row scroll.
      todayNavigationRef.current = request.firstIncomplete ? null : {id};
    } else if (['setup','empty','rest'].includes(selectedAction.kind)) {
      if (selectedAction.kind === 'setup' && modalService) modalService.open(PlanWizard, {modalService, backendService, isOnboarding: true, onPlanCreated: refreshActivities});
      else onOpenPlanEditor?.({});
    } else showTodayActivities();
  };
  const todayActionProps = {
    locked: Boolean(activityInteractionLock), refreshing: todayRefreshing, onAction: handleTodayAction,
  };

  const coveredDojoSource = useMemo(() => coveredDojoSourceActivities(
    dojoFeedData.activities, oldestPersonalSourceDate, hasMore, getDateKeyForUser,
  ), [dojoFeedData.activities, oldestPersonalSourceDate, hasMore, getDateKeyForUser]);
  // Dojo catch-up rows can be projected onto a newer day. Filter their
  // scheduled source before projection so a late peer completion cannot run
  // ahead of the corresponding personal page.
  const dojoByDate = useMemo(() => projectDojoActivitiesByDate(
    coveredDojoSource, getDateKeyForUser, new Date(expiryRevision),
  ), [coveredDojoSource, getDateKeyForUser, expiryRevision]);
  const coveredDojoByDate = useMemo(() => coveredDojoDays(
    dojoByDate, oldestPersonalSourceDate, hasMore, getDateKeyForUser,
  ), [dojoByDate, oldestPersonalSourceDate, hasMore, getDateKeyForUser]);

  // The primary feed owns startup day structure: an early camera answer is
  // staged (not rendered) until the first primary page settles successfully,
  // and a failed primary start keeps its own error/retry without camera rows.
  const primaryFeedReady = initialLoadComplete && !(activityLoadError && activities.length === 0);
  const groupedActivities = useMemo(() => {
    // Fallback targets from current profile (used only if backend doesn't provide per-meal targets)
    const fallbackTargets = userProfile?.nutrition_targets || null;
    
    const todayDate = new Date();
    const todayKey = todayKeyForAction;
    const todayFormatted = formatUserDate(todayDate, undefined, {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
    const now = Date.now();

    // Determine which day an activity should be displayed in.
    // Activities completed on a later day appear on the completion day;
    // pending/ongoing non-expired *scheduled* activities from earlier days
    // roll up into today's group so the user sees what's still actionable;
    // expired activities stay on their planned (created_at) day.
    //
    // We only roll over activities that represent a planned, scheduled
    // to-do (i.e. they have a `due_at`). Activities without a `due_at`
    // are records of events that already happened (meals logged, water
    // logged, status changes, bodyweight entries, etc.) and must stay on
    // their `created_at` day even if `status !== 'complete'` — otherwise
    // yesterday's logged consumption would silently migrate to today.
    const getDisplayDate = (activity) => {
      // Camera parents keep their capture/scheduled day after a later completion.
      if (isCameraMediaActivity(activity)) return new Date(cameraPlacementDate(activity));
      const isComplete = activity.status === 'complete';
      const dueAt = activity.due_at ? new Date(activity.due_at).getTime() : null;
      const isExpired = dueAt && dueAt < now && !isComplete;

      // Meals are point-in-time intake events: the foods they contain
      // belong to the day the meal was created, not the day the user
      // happened to mark it complete. Otherwise a water-only meal that
      // gets completed today silently moves all of yesterday's water
      // into today's daily nutrition pill (without affecting macros,
      // which is why the symptom looked water-specific).
      if (activity.type === 'meal') {
        return new Date(activity.created_at);
      }

      if (isComplete && activity.completed_at) {
        return new Date(activity.completed_at);
      }
      // Only scheduled (has due_at) activities roll forward to today.
      if (dueAt && !isComplete && !isExpired) {
        const createdKey = getDateKeyForUser(activity.created_at);
        if (createdKey < todayKey) {
          return todayDate;
        }
      }
      return new Date(activity.created_at);
    };

    // Apply the selected type/status filter at the feed boundary as a defense
    // against live WebSocket inserts and any backend pagination inconsistencies.
    const filteredActivities = filterActivitiesForFeed(
      parentTravelReplacementActivities(mergeCameraMedia(activities, primaryFeedReady ? cameraMedia.items : [], {
        boundary: videoScope === 'mine' && hasMore ? oldestPersonalSourceDate : null, startDate: filters.startDate, endDate: filters.endDate, dateKey: getDateKeyForUser,
      })),
      filters,
    );
    // Explicit, local reveal; never write FilterDropdown/localStorage preferences.
    // Canonical records win only for the requested occurrence/current day.
    const revealed = parentTravelReplacementActivities(activities).filter(a =>
      (todayReveal?.id && todayActivityId(a) === todayReveal.id)
      || (showTodayOverride && getDateKeyForUser(getDisplayDate(a)) === todayKey)
    );
    revealed.forEach(a => {
      const index = filteredActivities.findIndex(row => todayActivityId(row) === todayActivityId(a));
      if (index < 0) filteredActivities.push(activities.find(row => todayActivityId(row) === todayActivityId(a)) || a);
    });

    // Deduplicate activities by _id to prevent duplicate key warnings
    const seenIds = new Set();
    const uniqueActivities = filteredActivities.filter(activity => {
      if (seenIds.has(activity._id)) {
        return false;
      }
      seenIds.add(activity._id);
      return true;
    });

    // Helper: for a completed workout, determine which dates its sets span
    const getWorkoutSetDateKeys = (workout) => {
      const exercises = workout.data?.exercises || [];
      const dateMap = new Map(); // dateKey -> { calories, setCount }
      const workoutFallbackDate = workout.completed_at;
      for (const ex of exercises) {
        for (const set of (ex.sets || [])) {
          if (!set.complete) continue;
          const timestamp = set.completed_at || workoutFallbackDate;
          if (!timestamp) continue;
          const dk = getDateKeyForUser(timestamp);
          const entry = dateMap.get(dk) || { calories: 0, setCount: 0 };
          entry.setCount++;
          if (Number.isFinite(set.estimated_calories) && set.estimated_calories > 0) {
            entry.calories += set.estimated_calories;
          }
          dateMap.set(dk, entry);
        }
      }
      return dateMap.size > 0 ? dateMap : null;
    };

    // Helper: add an activity to a day group
    const addToGroup = (activity, dateKey, formattedDate) => {
      if (!groupMap.has(dateKey)) {
        const date = formattedDate || formatUserDate(new Date(dateKey + 'T12:00:00'), undefined, {
          weekday: 'long',
          year: 'numeric',
          month: 'long',
          day: 'numeric'
        });
        groupMap.set(dateKey, {
          date,
          dateKey,
          types: {},
          nutrition: { calories: 0, protein: 0, carbs: 0, fat: 0, water_ml: 0 },
          workoutBurned: 0
        });
      }
      const group = groupMap.get(dateKey);

      if (activity.type === 'meal' && activity.nutrition_summary) {
        group.nutrition.calories += (activity.nutrition_summary.calories || 0);
        group.nutrition.protein += (activity.nutrition_summary.protein || 0);
        group.nutrition.carbs += (activity.nutrition_summary.carbs || 0);
        group.nutrition.fat += (activity.nutrition_summary.fat || 0);
        if (!group.nutritionTargets && activity.nutrition_targets) {
          group.nutritionTargets = activity.nutrition_targets;
        }
      }

      // Accumulate workout calories burned per group
      if (activity.type === 'workout' && activity._partCalories != null) {
        group.workoutBurned += activity._partCalories;
      } else if (activity.type === 'workout' && activity.status === 'complete' && Number.isFinite(activity.calories) && activity.calories > 0 && !activity._partDateFilter) {
        group.workoutBurned += activity.calories;
      }

      const type = activity.type || 'other';
      if (!group.types[type]) {
        group.types[type] = [];
      }
      group.types[type].push(activity);
    };

    // Group activities by display date using a Map (handles activities that move
    // to a different day without creating duplicate day groups).
    const groupMap = new Map();

    uniqueActivities.forEach(activity => {
      // For completed workouts, check if sets span multiple days
      if (activity.type === 'workout' && activity.status === 'complete') {
        const setDateKeys = getWorkoutSetDateKeys(activity);
        if (setDateKeys && setDateKeys.size > 1) {
          // Multi-day workout: create virtual entries for each day
          const sortedDates = [...setDateKeys.keys()].sort();
          sortedDates.forEach((dk, index) => {
            const info = setDateKeys.get(dk);
            const virtualEntry = {
              ...activity,
              _virtualId: `${activity._id}_part_${dk}`,
              _partDateFilter: dk,
              _partNumber: index + 1,
              _totalParts: sortedDates.length,
              _partCalories: Math.round(info.calories)
            };
            addToGroup(virtualEntry, dk);
          });
          return;
        }
        // Single-day workout with set timestamps: use set-level calories and set date
        if (setDateKeys && setDateKeys.size === 1) {
          const [dk, info] = [...setDateKeys.entries()][0];
          const entry = {
            ...activity,
            _partCalories: Math.round(info.calories)
          };
          addToGroup(entry, dk, formatUserDate(new Date(dk + 'T12:00:00'), undefined, {
            weekday: 'long',
            year: 'numeric',
            month: 'long',
            day: 'numeric'
          }));
          return;
        }
      }

      const captureDay = cameraCaptureDay(activity);
      if (captureDay) {
        // Format a civil date in UTC solely for its calendar label; the saved
        // owner's day never shifts when the viewer changes profile timezone.
        const label = new Date(`${captureDay}T12:00:00.000Z`).toLocaleDateString(undefined, {
          timeZone: 'UTC', weekday: 'long', year: 'numeric', month: 'long', day: 'numeric',
        });
        addToGroup(activity, captureDay, label);
        return;
      }
      // Default path: non-workout or workout without set timestamps
      const activityDate = getDisplayDate(activity);
      const dateKey = getDateKeyForUser(activityDate);
      const date = formatUserDate(activityDate, undefined, {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });

      if (!groupMap.has(dateKey)) {
        groupMap.set(dateKey, {
          date: date,
          dateKey: dateKey,
          types: {},
          nutrition: { calories: 0, protein: 0, carbs: 0, fat: 0, water_ml: 0 },
          workoutBurned: 0
        });
      }

      const group = groupMap.get(dateKey);
      
      // Accumulate nutrition from MEALS
      if (activity.type === 'meal') {
           const summary = activity.nutrition_summary || {};
           // Trust backend totals — they use authoritative DB lookups for
           // each food (resolving serving_size/serving_unit correctly),
           // whereas the per-food snapshot stored on the meal can have
           // serving_size: 1 / serving_unit: "serving" without a
           // serving_weight, which makes any cross-unit (e.g. grams)
           // conversion blow up the macros — water especially, since
           // many real foods have a non-trivial water_ml content.
           group.nutrition.calories += (summary.calories || 0);
           group.nutrition.protein += (summary.protein || 0);
           group.nutrition.carbs += (summary.carbs || 0);
           group.nutrition.fat += (summary.fat || 0);
           group.nutrition.water_ml += (summary.water_ml || 0);

           // Capture per-day nutrition targets from the first meal that has them
           if (!group.nutritionTargets && activity.nutrition_targets) {
             group.nutritionTargets = activity.nutrition_targets;
           }
      }

      // Keep workout energy expenditure visible next to daily meal tallies.
      if (activity.type === 'workout' && activity.status === 'complete' && Number.isFinite(activity.calories) && activity.calories > 0) {
        group.workoutBurned += activity.calories;
      }

      const type = activity.type || 'other';
      if (!group.types[type]) {
          group.types[type] = [];
      }
      group.types[type].push(activity);
    });

    // Sort groups by dateKey descending (newest first)
    const groups = Array.from(groupMap.values()).sort((a, b) => b.dateKey.localeCompare(a.dateKey));

    // Ensure today always has a day group (so the add-activity button renders even with 0 activities)
    if (!groupMap.has(todayKey)) {
      groups.unshift({
        date: todayFormatted,
        dateKey: todayKey,
        types: {},
        nutrition: { calories: 0, protein: 0, carbs: 0, fat: 0, water_ml: 0 },
        workoutBurned: 0
      });
    }

    // Post-process scores
    groups.forEach(group => {
       // For today, always use the live profile targets (reflects current calories burned).
       // For past days, prefer the snapshot baked into meal activities.
       const isToday = group.dateKey === todayKey;
       const targets = isToday
         ? (fallbackTargets || group.nutritionTargets)
         : group.nutritionTargets;
       if (targets) {
           group.nutritionTargets = targets;
           group.complianceScore = calculateComplianceScore(group.nutrition, targets);
       }
       
       Object.keys(group.types).forEach(type => {
        group.types[type].sort((a, b) => compareActivitiesForDisplay(type, a, b));
      });
    });

    return addHistoricalDojoDayGroups(groups, {
      personalActivities: activities,
      currentUserId: userProfile?._id || userProfile?.id || userProfile?.user_id,
      dojoUsers: dojoFeedData.users,
      dojoByDate: coveredDojoByDate,
      todayKey,
      getDateKey: getDateKeyForUser,
      formatDate: dateKey => new Date(`${dateKey}T12:00:00Z`).toLocaleDateString(undefined, {
        timeZone: 'UTC', weekday: 'long', year: 'numeric', month: 'long', day: 'numeric',
      }),
      filters,
    });
  }, [activities, cameraMedia.items, primaryFeedReady, videoScope, hasMore, oldestPersonalSourceDate, filters, userProfile, getDateKeyForUser, formatUserDate, coveredDojoByDate, dojoFeedData.users, todayReveal, showTodayOverride, todayKeyForAction]);

  // A ref detach must never update React state: old/new first-day callbacks
  // can disagree during Live insertion/reordering. Derive once from committed groups.
  useEffect(() => {
    setHasTodayInFeed(groupedActivities[0]?.dateKey === todayKeyForAction);
  }, [groupedActivities, todayKeyForAction]);

  useEffect(() => {
    const request = todayNavigationRef.current;
    if (!request || activityInteractionLock) return undefined;
    let frame, attempts = 0;
    const focus = () => {
      const row = request.id ? document.getElementById(`activity-${request.id}`) : document.getElementById('today-activities');
      if (row && (!request.id || row.classList.contains('expanded'))) {
        row.focus({preventScroll:true});
        const behavior = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
        if (!request.id) scrollPageIntoView(row, { block: 'start', behavior });
        else scrollPageTo({top:Math.max(0,row.getBoundingClientRect().top + window.scrollY - JUMP_OFFSET), behavior});
        todayNavigationRef.current = null;
      } else if (++attempts < 48) frame = requestAnimationFrame(focus);
      else {
        todayNavigationRef.current = null;
      }
    };
    frame = requestAnimationFrame(focus);
    return () => cancelAnimationFrame(frame);
  }, [groupedActivities, activityInteractionLock]);

  // Pending activities per type, in feed render order.
  const pendingActivitiesByType = useMemo(() => {
    const map = new Map();
    groupedActivities.forEach(group => {
      ORDERED_TYPES.forEach(type => {
        (group.types?.[type] || []).forEach(activity => {
          if (!isPendingActivity(activity)) return;
          if (!map.has(type)) map.set(type, []);
          map.get(type).push(activity);
        });
      });
    });
    return map;
  }, [groupedActivities]);

  const pendingIdsByType = useMemo(() => {
    const map = new Map();
    pendingActivitiesByType.forEach((activities, type) => {
      map.set(type, activities.map(activity => activity._virtualId || activity._id));
    });
    return map;
  }, [pendingActivitiesByType]);

  // A workout in progress takes over the feed: nothing else is worth being
  // pointed at until it is done.
  const ongoingWorkoutId = useMemo(() => {
    for (const group of groupedActivities) {
      const ongoing = (group.types?.workout || []).find(a => ONGOING_STATUSES.includes(a.status));
      if (ongoing) return ongoing._virtualId || ongoing._id;
    }
    return null;
  }, [groupedActivities]);

  // Tracks which pending rows are on screen, which drives both the "n of m"
  // readout and the cycling anchor.
  const [visiblePendingIds, setVisiblePendingIds] = useState(() => new Set());
  const [focusedPendingId, setFocusedPendingId] = useState(null);

  useEffect(() => {
    const ids = [...pendingIdsByType.values()].flat();
    if (ongoingWorkoutId && !ids.includes(ongoingWorkoutId)) ids.push(ongoingWorkoutId);
    if (ids.length === 0) {
      setVisiblePendingIds(prev => (prev.size === 0 ? prev : new Set()));
      setFocusedPendingId(null);
      return;
    }

    const visible = new Set();
    const updateFocus = () => {
      const viewportCenter = window.innerHeight / 2;
      let closestId = null;
      let closestDistance = Infinity;

      visible.forEach(id => {
        const row = document.getElementById(`activity-${id}`);
        if (!row) return;
        const rect = row.getBoundingClientRect();
        if (rect.bottom <= 0 || rect.top >= window.innerHeight) return;
        const distance = Math.abs((rect.top + rect.bottom) / 2 - viewportCenter);
        if (distance < closestDistance) {
          closestDistance = distance;
          closestId = id;
        }
      });

      setFocusedPendingId(closestId);
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        const id = entry.target.id.replace(/^activity-/, '');
        if (entry.isIntersecting) visible.add(id);
        else visible.delete(id);
      });
      setVisiblePendingIds(prev => {
        if (prev.size === visible.size && [...visible].every(id => prev.has(id))) return prev;
        return new Set(visible);
      });
      updateFocus();
    }, {
      // Rows in the band behind the TopBar don't count as being in view. This
      // has to clear JUMP_OFFSET so the row we just scrolled away from stops
      // counting, otherwise it stays the anchor and the next tap goes nowhere.
      rootMargin: `-${JUMP_OFFSET - 30}px 0px 0px 0px`,
      threshold: 0
    });

    ids.forEach(id => {
      const el = document.getElementById(`activity-${id}`);
      if (el) observer.observe(el);
    });

    window.addEventListener('scroll', updateFocus, { passive: true });
    window.addEventListener('resize', updateFocus);
    requestAnimationFrame(updateFocus);

    return () => {
      observer.disconnect();
      window.removeEventListener('scroll', updateFocus);
      window.removeEventListener('resize', updateFocus);
    };
  }, [pendingIdsByType, ongoingWorkoutId]);

  const pendingJumpTypes = useMemo(
    () => ORDERED_TYPES.filter(type => (pendingIdsByType.get(type) || []).length > 0),
    [pendingIdsByType]
  );

  const pendingJumpActivities = useMemo(
    () => pendingJumpTypes.flatMap(type => (
      (pendingActivitiesByType.get(type) || []).map(activity => ({ activity, type }))
    )),
    [pendingJumpTypes, pendingActivitiesByType]
  );

  // Returns false when the row isn't mounted yet (below the loaded page).
  const focusActivityRow = useCallback((id) => {
    const el = document.getElementById(`activity-${id}`);
    if (!el) return false;

    const y = el.getBoundingClientRect().top + window.pageYOffset - JUMP_OFFSET;
    scrollPageTo({ top: y, behavior: 'smooth' });

    el.classList.remove('jump-flash');
    void el.offsetWidth; // restart the animation when re-targeting the same row
    el.classList.add('jump-flash');
    setTimeout(() => el.classList.remove('jump-flash'), 1400);
    return true;
  }, []);

  const renderJumpButton = (activity, type) => {
    if (!activity || !type) return null;
    const config = TYPE_CONFIG[type] || TYPE_CONFIG.other;
    const id = activity._virtualId || activity._id;
    const isFocused = focusedPendingId === id;
    const label = activity.name?.trim() || config.label;
    return (
      <button
        key={id}
        className={`jump-to-pending-btn ${isFocused ? 'focused' : 'dimmed'}`}
        style={{ '--jump-color': config.color }}
        onClick={() => focusActivityRow(id)}
        aria-label={`Go to unfinished ${label.toLowerCase()}`}
        title={`Go to this unfinished ${label.toLowerCase()}`}
      >
        <span className="jump-to-pending-icon" aria-hidden>{config.icon}</span>
        <span className="jump-to-pending-label">{label}</span>
      </button>
    );
  };

  // Dynamic Title based on scroll
  useEffect(() => {
    // If modal is open, let App.js handle title
    if (modalService && modalService.modals && modalService.modals.length > 0) return;
    
    // Clear previous visibility state on re-init
    visibleDaysMap.current.clear();

    const observer = new IntersectionObserver((entries) => {
        let needsUpdate = false;
        entries.forEach(entry => {
            const dateStr = entry.target.getAttribute('data-date');
            if (!dateStr) return;
            
            if (entry.isIntersecting) {
                visibleDaysMap.current.set(dateStr, entry.target);
            } else {
                visibleDaysMap.current.delete(dateStr);
            }
            needsUpdate = true;
        });

        if (needsUpdate) {
            let coveringTop = null;
            let firstIncoming = null;
            let minIncomingTop = Infinity;
            
            visibleDaysMap.current.forEach((el, dateStr) => {
                const rect = el.getBoundingClientRect();
                
                // Check if covering "eye level" (e.g. 150px from top)
                if (rect.top <= 150 && rect.bottom > 150) {
                    coveringTop = { el, dateStr };
                }
                
                if (rect.top > 150 && rect.top < minIncomingTop) {
                    minIncomingTop = rect.top;
                    firstIncoming = { el, dateStr };
                }
            });
            
            const winner = coveringTop || firstIncoming;
            
            if (winner) {
                // Check if it's today
                const todayStr = formatUserDate(new Date(), undefined, {
                  weekday: 'long', month: 'long', day: 'numeric', year: 'numeric'
                });
                
                let titleDate = winner.dateStr;
                
                if (winner.dateStr === todayStr) {
                    titleDate = "Today";
                } else {
                    // Format "Friday, February 3, 2026" -> "Feb 3"
                    const parts = winner.dateStr.split(','); 
                    if (parts.length >= 2) {
                        try {
                           const datePart = parts.slice(1).join(',').trim();
                           const d = new Date(datePart);
                           if (!isNaN(d)) {
                                 titleDate = formatUserDate(d, 'en-US', { month: 'short', day: 'numeric' });
                           }
                        } catch (e) { /* fallback to full string */ }
                    }
                }
                
                document.title = `${titleDate} | Kata.fit`;
            }
        }
    }, { threshold: [0, 0.1] });
    
    dayGroupRefs.current.forEach(el => observer.observe(el));
    
    return () => {
        observer.disconnect();
    };
    
  }, [groupedActivities, modalService, formatUserDate]);

  // Check if user is brand new (zero activities) - only after initial load
  const isNewUser = initialLoadComplete && hasAnyActivities === false;

  // Get user-specific localStorage key for onboarding
  const getOnboardingKey = useCallback(() => {
    const userId = userProfile?._id || userProfile?.id;
    return userId ? `onboardingComplete_${userId}` : null;
  }, [userProfile]);

  // State to track if onboarding story has been completed/dismissed
  const [onboardingComplete, setOnboardingComplete] = useState(null);

  // Load onboarding state when userProfile becomes available
  useEffect(() => {
    const key = getOnboardingKey();
    if (key) {
      setOnboardingComplete(localStorage.getItem(key) === 'true');
    }
  }, [getOnboardingKey]);

  const handleOnboardingComplete = useCallback(() => {
    setOnboardingComplete(true);
    const key = getOnboardingKey();
    if (key) {
      localStorage.setItem(key, 'true');
    }
  }, [getOnboardingKey]);

  const refreshActivities = useCallback(async () => {
    // Reconcile instead of clearing + replacing. The old path did
    // setActivities([]) then setActivities(data), which blanked the list,
    // snapped the scroll back to the top, and remounted every row (losing any
    // uncommitted edits). Reconciling merges the fresh snapshot by _id in
    // place, so the viewport and any in-progress input edits are preserved.
    // Wait for the plan-affiliation write to be observable before fetching
    // activities; otherwise the old "No Workout Plan" state can win the race
    // immediately after dojo membership changes.
    try {
      let affiliation = null;
      for (let attempt = 0; attempt < 5; attempt += 1) {
        affiliation = await backendService.fetchUserAffiliation();
        if (affiliation?.activity_plan_id) break;
        await new Promise(resolve => setTimeout(resolve, 200));
      }
      setHasWorkoutPlan(affiliation && affiliation.activity_plan_id);
    } catch (e) {
      console.error("Failed to refresh user affiliation", e);
    }
    await fetchActivitiesRef.current?.(0, true, true);
  }, [backendService]);

  // Native sync may complete a planned metric after the foreground fetch.
  // Reconcile through the loaded range rather than resetting pagination/scroll.
  useEffect(() => {
    const handleHealthConnectSynced = event => {
      const receipt = event.detail;
      if (Array.isArray(receipt?.activities)) {
        const scope = workoutStartScope.current;
        const owner = userProfileRef.current?._id || userProfileRef.current?.id;
        if (!owner || String(receipt.userId) !== String(owner)
          || receipt.backendService !== scope.service
          || receipt.identity !== scope.service.authenticatedIdentity
          || receipt.positionOwner !== scope.service.positionOwner
          || receipt.identity !== scope.identity || receipt.positionOwner !== scope.positionOwner) return;
        receipt.activities.forEach(row => handleActivityRefreshRef.current?.(row, { canonicalMutation: true }));
        return;
      }
      fetchActivitiesRef.current?.(0, true, true);
    };
    window.addEventListener('health-connect-synced', handleHealthConnectSynced);
    return () => window.removeEventListener('health-connect-synced', handleHealthConnectSynced);
  }, []);

  // Refresh when the app wakes from background on a new day (mobile)
  useEffect(() => {
    let lastSeenDate = getDateKeyForUser(new Date());
    const handleWake = () => {
      if (document.visibilityState !== 'visible') return;
      const today = getDateKeyForUser(new Date());
      if (today !== lastSeenDate) {
        lastSeenDate = today;
        refreshActivities();
      }
    };
    document.addEventListener('visibilitychange', handleWake);
    return () => document.removeEventListener('visibilitychange', handleWake);
  }, [getDateKeyForUser, refreshActivities]);


  // Joining a dojo can materialize a dojo training plan and create scheduled
  // activities for the user. Leaving can remove that dojo context. Refresh
  // the activity state as well as the dojo feed so the feed and plan never
  // lag behind the membership mutation.
  useEffect(() => {
    if (dojoChangeVersion > 0) {
      refreshActivities();
    }
    // refreshActivities owns the fetch/reset behavior and is intentionally
    // excluded to avoid re-running this effect when its callback identity
    // changes with unrelated state.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dojoChangeVersion]);

  const startupPresentation = getActivityStartupPresentation({
    initialLoadComplete,
    activityLoadError,
    activityCount: activities.length,
    profileSettled,
    dojoStatusLoaded,
  });

  // Only show onboarding after both the activity and local-storage checks
  // confirm this is a new, incomplete account. Returning users render the
  // activity shell immediately instead of seeing a full-screen loading flash.
  if (isNewUser && onboardingComplete === false) {
    return (
      <div className="activity-list">
        <OnboardingStory
          onComplete={handleOnboardingComplete}
          onRefreshActivities={refreshActivities}
          onOpenPlanEditor={onOpenPlanEditor}
          openNewActivityModal={openNewActivityModal}
          modalService={modalService}
          backendService={backendService}
        />
      </div>
    );
  }

  const renderCoachContent = (dateKey, isToday) => <>
      {/* Dojo Hub — unified coach + member activity card (replaces separate DojoPulse + DayRecommendationCard).
          Do not render coach identity surfaces until the persisted profile has
          loaded; showing a robot/default name is worse than briefly showing
          no coach card. */}
      {isToday && startupPresentation.coachSlot !== 'content' ? (
        <div className={activityInteractionLock ? 'activity-global-controls-locked' : ''} inert={activityInteractionLock ? '' : undefined}>
        <CoachFeedLoadingSlot
          hasAiFeature={hasAiFeature}
          openCoachChat={activityInteractionLock ? undefined : openCoachChat}
          contextLoading={startupPresentation.coachSlot === 'context-loading'}
          aiCoachName={userProfile?.ai_coach_settings?.name}
          aiCoachEmoji={userProfile?.ai_coach_settings?.emoji}
          aiCoachImageUrl={getCoachImageVariantUrl(userProfile?.ai_coach_settings, 'avatar')}
          coachMessage={findLatestCoachMessage(coachHomePreview, recommendationsByDate[dateKey], { settled: coachHomePreviewSettled, dayKey: coachDayKey, timeZone: effectiveTimezone })}
          backendService={backendService}
          isTyping={inlineCoachTyping}
          dailyPending={dailyInsightPending}
        />
        </div>
      ) : userProfile && (dojoFeedData.dojoName ? (
        <div className={activityInteractionLock ? 'activity-global-controls-locked' : ''} inert={activityInteractionLock ? '' : undefined}>
        <DojoHub
          dojoActivities={coveredDojoByDate[dateKey] || []}
          dojoUsers={dojoFeedData.users}
          dojoName={dojoFeedData.dojoName}
          backendService={backendService}
          recommendation={recommendationsByDate[dateKey]}
          coachMessage={isToday
            ? findLatestCoachMessage(coachHomePreview, recommendationsByDate[dateKey], { settled: coachHomePreviewSettled, dayKey: coachDayKey, timeZone: effectiveTimezone })
            : coachMessageFromInsight(recommendationsByDate[dateKey])}
          coachMessageSettled={!isToday || coachHomePreviewSettled}
          isToday={isToday}
          isGenerating={isToday && isGeneratingRec}
          dailyPending={isToday && dailyInsightPending}
          isThinking={isToday && inlineCoachTyping}
          openRecommendations={openRecommendations}
          aiCoachName={userProfile?.ai_coach_settings?.name}
          aiCoachEmoji={userProfile?.ai_coach_settings?.emoji}
          aiCoachImageUrl={getCoachImageVariantUrl(userProfile?.ai_coach_settings, 'avatar')}
          hasAiFeature={hasAiFeature}
          openUpgrade={openUpgrade}
          openCoachChat={openCoachChat}
        />
        </div>
      ) : (
        <div className={activityInteractionLock ? 'activity-global-controls-locked' : ''} inert={activityInteractionLock ? '' : undefined}>
        <DayRecommendationCard
          recommendation={recommendationsByDate[dateKey]}
          coachHomePreview={coachHomePreview}
          coachHomePreviewSettled={coachHomePreviewSettled}
          coachDayKey={coachDayKey}
          timeZone={effectiveTimezone}
          isToday={isToday}
          isGenerating={isToday && isGeneratingRec}
          dailyPending={isToday && dailyInsightPending}
          openRecommendations={openRecommendations}
          aiCoachEmoji={userProfile?.ai_coach_settings?.emoji}
          aiCoachName={userProfile?.ai_coach_settings?.name}
          aiCoachImageUrl={getCoachImageVariantUrl(userProfile?.ai_coach_settings, 'avatar')}
          hasAiFeature={hasAiFeature}
          openUpgrade={openUpgrade}
          backendService={backendService}
          openCoachChat={openCoachChat}
          isThinking={isToday && inlineCoachTyping}
          onRegenerate={regenerateDailyInsight}
        />
        </div>
      ))}

  </>;

  return (
    <>
    {activityShortcutTarget && createPortal(
      <TopBarActivityActions
        key={`${userProfile?._id || userProfile?.id || userProfile?.user_id || ''}:${todayKeyForAction}`}
        loading={!primaryTodayLoadFailed && todayAction.kind === 'loading'}
        actions={headerTodayActions}
        ongoingWorkoutId={ongoingShortcutWorkoutId(todayActions, activities, cancelledShortcutWorkoutId, todayNow)}
        completedIds={!todayRefreshing && todaySnapshot.loaded && !todaySnapshot.error && !todaySnapshot.partial
          && todaySnapshot.dayKey === todayKeyForAction
          ? todaySnapshot.activities.filter(a => a.status === 'complete').map(todayActivityId) : []}
        now={todayNow}
        getDateKey={getDateKeyForUser}
        {...todayActionProps}
      />, activityShortcutTarget)}
    <FilterDropdown ref={filterControlRef} hideTrigger disabled={Boolean(activityInteractionLock)} onFilterChange={handleFilterChange} onSummaryChange={setFilterSummary} videoScope={videoScope} onVideoScopeChange={setVideoScope} />
    <CoachVisibilityProvider key={userProfile?._id || userProfile?.id || 'anonymous'}>
    <div className={`activity-list ${backgroundImageUrl ? 'dojo-branded-feed' : ''}`}
      style={backgroundImageUrl ? {
        '--dojo-feed-background': `url("${backgroundImageUrl}")`,
        '--dojo-feed-position': `${backgroundFocalPoint?.x ?? 50}% ${backgroundFocalPoint?.y ?? 50}%`,
      } : undefined}>
       {/* One shared top-toast stack: activity and coach-answer
           notifications participate in the same flex layout and reflow as
           entries appear/disappear. */}
       {(dojoPills.length > 0 || coachReadyToast || (coachReviewToasts.length > 0 && !coachHubOpen) || coachReactionToast || isGeneratingRec || dailyCoachPending || dailyCoachFailed || dailyCoachRetryUncertain || (unscopedActivityReviewPending && !coachHubOpen) || (askCoachTyping && !coachHubOpen)) && (
         <div className="dojo-toast-container unified-toast-stack">
           {dojoPills.map(pill => (
             <DojoActivityToast key={`activity-${pill.id}`} pill={pill} backendService={backendService} onActivityClick={handleDojoActivityClick} />
           ))}

           {coachReactionToast && (
             <div
               className="dojo-toast coach-reaction-toast"
               style={{ '--toast-accent': '#8b5cf6', '--toast-timer-duration': '8s' }}
               role="button"
               tabIndex={0}
               onClick={() => { clearCoachReactionToast(coachReactionToast.eventId); openCoachChat?.(coachToastFocusOptions(coachReactionToast.eventId)); }}
               onKeyDown={event => {
                 if (event.key === 'Enter' || event.key === ' ') {
                   event.preventDefault();
                   clearCoachReactionToast(coachReactionToast.eventId);
                   openCoachChat?.(coachToastFocusOptions(coachReactionToast.eventId));
                 }
               }}
             >
               <div className="dojo-toast-accent" />
               <div className="dojo-toast-avatar">
                 <CoachAvatar
                   imageUrl={getCoachImageVariantUrl(userProfile?.ai_coach_settings, 'avatar')}
                   emoji={userProfile?.ai_coach_settings?.emoji || '🥋'}
                   identitySettled={profileSettled}
                   eager
                 />
               </div>
               <div className="dojo-toast-body">
                 <span className="dojo-toast-message">{userProfile?.ai_coach_settings?.name || 'Coach'} reacted to {coachReactionToast.activityName}</span>
                 <span className="dojo-toast-coach-highlight" ref={element => { if (element) noteCoachOutputCommitted('activity_review', String(coachReactionToast.eventId || ''), element, 'reaction_toast'); }}>{coachReactionToast.reaction?.emoji} {coachReactionToast.reaction?.label}</span>
               </div>
               <div className="dojo-toast-timer" />
             </div>
           )}

           <DailyInsightToastGate dailyPending={dailyInsightPending} unrelatedPending={unrelatedCoachToastPending}>
             <div className="dojo-toast coach-typing-toast" role="status" aria-label={unrelatedCoachToastPending ? `${userProfile?.ai_coach_settings?.name || 'Coach'} is typing` : 'Preparing your daily insight'} onClick={() => openCoachChat?.()}>
               <div className="dojo-toast-accent" style={{ '--toast-accent': '#8b5cf6' }} />
               <div className="dojo-toast-avatar">
                 <CoachAvatar
                   imageUrl={getCoachImageVariantUrl(userProfile?.ai_coach_settings, 'avatar')}
                   emoji={userProfile?.ai_coach_settings?.emoji || '🥋'}
                   identitySettled={profileSettled}
                   eager
                 />
               </div>
               <div className="dojo-toast-body coach-typing-toast-body">
                 {!unrelatedCoachToastPending && <span className="daily-insight-toast-label">Preparing your daily insight</span>}
                 <span className="coach-hub-typing-dot" />
                 <span className="coach-hub-typing-dot" />
                 <span className="coach-hub-typing-dot" />
               </div>
             </div>
           </DailyInsightToastGate>

           {(dailyCoachFailed || dailyCoachRetryUncertain) && (
             <DailyCoachFailureNotice status={dailyCoachStatus} onOpenChat={() => openCoachChat?.({ showDailyCoachFailure: true })}
               onRetry={retryDailyCoach} retrying={dailyCoachRetrying} retryError={dailyCoachRetryError} retryUncertain={dailyCoachRetryUncertain} />
           )}

           {!coachHubOpen && coachReviewToasts.map(reviewToast => (
             <CoachToastGate key={reviewToast.key} typing={reviewToast.typing && !reviewToast.failed} insightId={reviewToast.failed || reviewToast.typing ? null : reviewToast.insightId} text={reviewToast.content}>
             <div
               key={reviewToast.key}
               className={`dojo-toast coach-ready-toast ${reviewToast.typing ? 'coach-typing-toast' : (reviewToast.failed ? 'coach-review-failed-toast' : 'dojo-toast-completion')} ${reviewToast.exiting ? 'exiting' : ''}`}
               style={{ '--toast-accent': reviewToast.typing ? '#8b5cf6' : (reviewToast.failed ? '#ef4444' : '#22c55e'), '--toast-timer-duration': '8s' }}
               onClick={() => openCoachChat?.(coachToastFocusOptions(reviewToast.eventId))}
               onKeyDown={(event) => {
                 if (event.key === 'Enter' || event.key === ' ') {
                   event.preventDefault();
                   openCoachChat?.(coachToastFocusOptions(reviewToast.eventId));
                 }
               }}
               role="button"
               tabIndex={0}
               aria-live="polite"
               aria-atomic="true"
               aria-label={reviewToast.typing
                 ? `${reviewToast.coachName || coachThinkingName || 'Coach'} is typing`
                 : (reviewToast.failed
                   ? `Coach review failed. Open Coach to retry`
                   : `Open message from ${reviewToast.coachName || coachThinkingName || 'Coach'}`)}
             >
               <div className="dojo-toast-accent" />
               <div className="dojo-toast-avatar">
                 <CoachAvatar
                   imageUrl={getCoachImageVariantUrl(userProfile?.ai_coach_settings, 'avatar')}
                   emoji={userProfile?.ai_coach_settings?.emoji || '🥋'}
                   identitySettled={profileSettled}
                   eager
                 />
               </div>
               {reviewToast.typing ? (
                 <div className="dojo-toast-body coach-typing-toast-body" aria-hidden="true">
                   <span className="coach-hub-typing-dot" />
                   <span className="coach-hub-typing-dot" />
                   <span className="coach-hub-typing-dot" />
                 </div>
               ) : (
                 <div className="dojo-toast-body">
                   <span className="coach-ready-toast-label">
                     {reviewToast.failed
                       ? `${reviewToast.coachName || coachThinkingName || 'Coach'} review failed`
                       : (reviewToast.coachName || coachThinkingName || 'Your coach')}
                   </span>
                   <span className="coach-ready-toast-answer" ref={element => { if (element && !reviewToast.failed) noteCoachOutputCommitted('activity_review', String(reviewToast.eventId || ''), element, 'review_answer_toast'); }}>{reviewToast.answer}</span>
                 </div>
               )}
               {reviewToast.failed && (
                 <button
                   type="button"
                   className="coach-review-toast-dismiss"
                   aria-label="Dismiss failed Coach review notification"
                   onClick={(event) => {
                     event.stopPropagation();
                     setCoachReviewToasts(current => removeCoachReviewToast(current, reviewToast.eventId));
                   }}
                 >
                   ×
                 </button>
               )}
               {!reviewToast.typing && !reviewToast.failed && <div className="dojo-toast-shimmer" />}
               {!reviewToast.typing && !reviewToast.failed && <div className="dojo-toast-timer" />}
             </div>
             </CoachToastGate>
           ))}

           {coachReadyToast && (
             <CoachToastGate insightId={coachReadyToast.focusTimelineEntryId} text={coachReadyToast.content}>
             <div
               key={coachReadyToast.key}
               className={`dojo-toast dojo-toast-completion coach-ready-toast ${coachReadyToast.exiting ? 'exiting' : ''}`}
               style={{ '--toast-accent': '#22c55e', '--toast-timer-duration': '8s' }}
               onClick={() => openCoachChat?.(coachToastFocusOptions(coachReadyToast.focusTimelineEntryId))}
               onKeyDown={(event) => {
                 if (event.key === 'Enter' || event.key === ' ') {
                   event.preventDefault();
                   openCoachChat?.(coachToastFocusOptions(coachReadyToast.focusTimelineEntryId));
                 }
               }}
               role="button"
               tabIndex={0}
             >
               <div className="dojo-toast-accent" />
               <div className="dojo-toast-avatar">
                 <CoachAvatar
                   imageUrl={getCoachImageVariantUrl(userProfile?.ai_coach_settings, 'avatar')}
                   emoji={userProfile?.ai_coach_settings?.emoji || '🥋'}
                   identitySettled={profileSettled}
                   eager
                 />
               </div>
               <div className="dojo-toast-body">
                 <span className="coach-ready-toast-label">{coachReadyToast.coachName || coachThinkingName || 'Your coach'}</span>
                 <span className="coach-ready-toast-answer">{coachReadyToast.answer}</span>
               </div>
               <div className="dojo-toast-shimmer" />
               <div className="dojo-toast-timer" />
             </div>
             </CoachToastGate>
           )}
         </div>
       )}

      <ActivitiesGlobalFeedHeader
        filterControl={filterSummary.active ? <><span>Filtered</span><span aria-hidden="true"> · </span><button type="button" disabled={Boolean(activityInteractionLock)} onClick={() => {
          filterControlRef.current?.clear();
          firstDayGroupRef.current?.focus({ preventScroll: true });
        }}>Clear</button></> : null}
        headingRef={firstDayGroupRef}
        statusControl={(
          <div className={activityInteractionLock ? 'activity-global-controls-locked' : ''} inert={activityInteractionLock ? '' : undefined}>
            <StatusDropdown
              currentStatus={currentStatus}
              onStatusChange={handleStatusChange}
              isSubmitting={isSubmittingStatus}
            />
          </div>
        )}
      >
        {renderCoachContent(todayKeyForAction, true)}
        <div className={activityInteractionLock ? 'activity-global-controls-locked' : ''} inert={activityInteractionLock ? '' : undefined}>
          <DailyInsightProposal backendService={backendService} recommendation={recommendationsByDate[todayKeyForAction]} isToday />
        </div>
        {todayIdentity && <div inert={activityInteractionLock ? '' : undefined}>{todayIdentity}</div>}
      </ActivitiesGlobalFeedHeader>

      {/* Dojo invite nudge — shown only when the user is not yet in a dojo.
       * Dismissible per session; can be permanently suppressed. Hidden once
       * the user joins/creates a dojo. Waits for membership to be resolved so
       * a dojo member doesn't see a brief flash of the nudge on load. */}
      {shouldShowDojoNudge({ statusLoaded: dojoStatusLoaded, inDojo: dojoFeedData.inDojo }) && (
        <DojoNudge
          onJoinOrCreate={openDojoTab}
          userId={userProfile?._id || userProfile?.id}
        />
      )}

      {/* Unified personal feed: social context is injected after each date. */}
      {(
        <InfiniteScroll
          dataLength={activityPaginationRevision}
          next={fetchMoreActivities}
          hasMore={hasMore}
          style={{ overflow: 'visible' }}
          loader={<ActivityPaginationLoading />}
          endMessage={<p style={{ textAlign: 'center' }} className="activity-end">No more activities to show</p>}
        >
          {groupedActivities.map((dayGroup, index) => {
            // Check if meals exist for nutrition summary
            const mealItems = dayGroup.types['meal'] || [];
            const hasMeals = mealItems.length > 0;
            
            // Check if this is today's date
            const todayStr = formatUserDate(new Date(), undefined, {
              weekday: 'long',
              year: 'numeric',
              month: 'long',
              day: 'numeric'
            });
            const displayDate = dayGroup.date === todayStr ? 'Today' : dayGroup.date;
            
            const isToday = displayDate === 'Today';
            // Water tracking launched on 2026-05-09. Days before that
            // legitimately have 0 ml logged — hide the empty bar on those
            // days to avoid visual noise. From the launch date onward we
            // always show the bar (even at 0) so the user can track it.
            const WATER_LAUNCH_DATE_KEY = '2026-05-09';
            const isWaterFeatureDay = dayGroup.dateKey >= WATER_LAUNCH_DATE_KEY;
            return (
              <div 
                key={dayGroup.date} 
                className="day-group"
                tabIndex={-1}
                // Data attribute for the intersection observer
                data-date={dayGroup.date}
                ref={(el) => {
                    // Update all day groups map for the title observer
                    if (el) {
                        dayGroupRefs.current.set(dayGroup.date, el);
                    } else {
                        dayGroupRefs.current.delete(dayGroup.date);
                    }
                }}
              >
                {!isToday && <p className="activity-date">{displayDate}</p>}

                {/* Keep the primary escape hatch for logging outside the
                    scheduled feed, where it is visible before today's rows. */}
                {isToday && (
                  <div className="add-activity-row add-activity-row-top">
                    <button
                      className="btn-add-activity-subtle"
                      onClick={openNewActivityModal}
                      disabled={Boolean(activityInteractionLock)}
                      title="Add custom activity"
                      aria-label="Add custom activity"
                    >
                      + Add custom activity
                    </button>
                  </div>
                )}

                <div className={activityInteractionLock ? 'activity-global-controls-locked' : ''} inert={activityInteractionLock ? '' : undefined}>
                  <SocialPresenceRow
                    presence={socialPresenceByDate[dayGroup.dateKey]}
                    revision={presenceRevision}
                    onOpen={handleSocialPresenceOpen}
                  />
                </div>
                
                {/* Historical status stays with its corresponding past day. */}
                {!isToday && dayStatuses[dayGroup.dateKey] && (() => {
                  const opt = STATUS_OPTIONS.find(s => s.value === dayStatuses[dayGroup.dateKey]);
                  return opt ? (
                    <div className="status-day-badge-container">
                      <div className="status-day-badge">
                        <span className="status-day-badge-emoji">{opt.emoji}</span>
                        <span className="status-day-badge-label">{opt.label}</span>
                      </div>
                    </div>
                  ) : null;
                })()}

                {!isToday && renderCoachContent(dayGroup.dateKey, false)}
                {isToday && startupPresentation.activitySlot === 'loading' ? (
                  <ActivityFeedLoadingSlot />
                ) : isToday && startupPresentation.activitySlot === 'error' ? (
                  <ActivityDataLoadError onRetry={retryInitialActivities} />
                ) : (
                  <>
                    {/* Collapsible Todo List with embedded activities. MoveFoodsProvider
                        is scoped per-day so move state stays within the date. */}
                    <WorkoutStartContext.Provider value={workoutStarts}>
                    <MoveFoodsProvider
                      backendService={backendService}
                      onActiveChange={active => handleFoodMoveModeChange(dayGroup.dateKey, active)}
                    >
                      <TodoList
                        filters={filters}
                        revealRequest={todayReveal}
                        dayGroup={dayGroup}
                        exercises={exercises}
                        modalService={modalService}
                        backendService={backendService}
                        onOpenPlanEditor={onOpenPlanEditor}
                        onActivityComplete={handleActivityCompletion}
                        onActivityCancel={handleActivityCancellation}
                        onActivityRefresh={handleActivityRefresh}
                        nutritionTotals={hasMeals ? {
                          calories: Math.round(dayGroup.nutrition.calories),
                          protein: Math.round(dayGroup.nutrition.protein),
                          carbs: Math.round(dayGroup.nutrition.carbs),
                          fat: Math.round(dayGroup.nutrition.fat),
                          water_ml: Math.round(dayGroup.nutrition.water_ml || 0)
                        } : null}
                        nutritionTargets={dayGroup.nutritionTargets}
                        complianceScore={dayGroup.complianceScore}
                        isRecentDay={isWaterFeatureDay}
                        getDateKeyForUser={getDateKeyForUser}
                        timezone={effectiveTimezone}
                        recommendationVersion={recommendationVersion}
                        coachImageUrl={getCoachImageVariantUrl(userProfile?.ai_coach_settings, 'avatar')}
                        coachEmoji={userProfile?.ai_coach_settings?.emoji || '🥋'}
                        coachIdentitySettled={profileSettled}
                        travelReplacementEnabled={travelReplacementEnabled}
                        activityInteractionLock={activityInteractionLock}
                        onWorkoutReorderModeChange={handleWorkoutReorderModeChange}
                        dojoNutritionActivities={coveredDojoByDate[dayGroup.dateKey] ? coveredDojoSource : []}
                        dojoActivities={coveredDojoByDate[dayGroup.dateKey] || []}
                        dojoUsers={dojoFeedData.users}
                        currentUserId={userProfile?._id || userProfile?.id || userProfile?.user_id}
                        onCameraMediaStatus={cameraMedia.setStatus}
                        cameraMediaMutation={cameraMedia.mutation}
                      />
                    </MoveFoodsProvider>
                    </WorkoutStartContext.Provider>

                  </>
                )}
              </div>
            );
          })}
        </InfiniteScroll>
      )}
      <CameraMediaFeedStatus feed={cameraMedia} />

      {/* A workout in progress is the only thing worth pointing at, and only
          once it has scrolled off screen. */}
      {ENABLE_ACTIVITY_JUMP_BUTTONS && ongoingWorkoutId && !visiblePendingIds.has(ongoingWorkoutId) && (
        <div className="feed-jump-stack">
          <button
            className="jump-to-pending-btn jump-to-ongoing"
            style={{ '--jump-color': TYPE_CONFIG.workout.color }}
            onClick={() => focusActivityRow(ongoingWorkoutId)}
            aria-label="Back to the workout in progress"
            title="Workout in progress — tap to go back to it"
          >
            <span className="jump-to-pending-icon" aria-hidden>{TYPE_CONFIG.workout.icon}</span>
            <span className="jump-to-pending-label">Resume</span>
          </button>
        </div>
      )}

      {/* Floating jump buttons for unfinished work. With nothing pending,
          the feed falls back to a plain "back to today" button. */}
      {ENABLE_ACTIVITY_JUMP_BUTTONS && !ongoingWorkoutId && (pendingJumpTypes.length > 0 || showScrollToTop) && (
        <div className={`feed-jump-stack ${focusedPendingId ? 'has-focus' : ''}`}>
          {focusedPendingId ? (
            <>
              <div className="jump-side jump-side-left">
                {pendingJumpActivities
                  .slice(0, pendingJumpActivities.findIndex(({ activity }) => (activity._virtualId || activity._id) === focusedPendingId))
                  .map(({ activity, type }) => renderJumpButton(activity, type))}
              </div>
              {renderJumpButton(
                pendingJumpActivities.find(({ activity }) => (activity._virtualId || activity._id) === focusedPendingId)?.activity,
                pendingJumpActivities.find(({ activity }) => (activity._virtualId || activity._id) === focusedPendingId)?.type
              )}
              <div className="jump-side jump-side-right">
                {pendingJumpActivities
                  .slice(pendingJumpActivities.findIndex(({ activity }) => (activity._virtualId || activity._id) === focusedPendingId) + 1)
                  .map(({ activity, type }) => renderJumpButton(activity, type))}
              </div>
            </>
          ) : (
            pendingJumpActivities.map(({ activity, type }) => renderJumpButton(activity, type))
          )}
          {pendingJumpTypes.length === 0 && showScrollToTop && (
            <button
              className="scroll-to-top-btn"
              onClick={scrollToTop}
              aria-label="Scroll to top"
            >
              <span className="scroll-to-top-arrow">↑</span>
              <span className="scroll-to-top-text">{hasTodayInFeed ? 'Today' : 'Top'}</span>
            </button>
          )}
        </div>
      )}
    </div>
    </CoachVisibilityProvider>
    </>
  );
};

export default Activities;
